/**
 * CashStatementPage Component
 * 
 * Nova página de extrato de caixa com sincronização bancária OFX.
 * Substitui a página antiga de fluxo de caixa com funcionalidades de:
 * - Importação de arquivos OFX
 * - Sincronização com contas bancárias
 * - Visualização de transações do extrato
 * - Conciliação com operações do ERP
 * - Validação de integridade de saldo
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { 
  Upload, Search, Filter, Calendar,
  ArrowUpCircle, ArrowDownCircle, CheckCircle2, 
  AlertCircle, Loader2, Landmark, CreditCard,
  X, ChevronRight, Download,
  FileText, Plus, Minus, Sparkles, Building2,
  MoreVertical, Link as LinkIcon,
  ChevronDown, Globe, Check, Trash2, Layers,
  Hash, ArrowRight, ArrowRightLeft, Pencil, CircleDashed, CircleCheck
} from 'lucide-react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { 
  cashStatementService, 
  financialAccountService,
  accountReceivableService,
  accountPayableService,
  cardReconciliationService,
  financialCategoryService,
  costCenterService
} from '../../../../services/erpApi'
import { formatDateOnly, toDateInputValue } from '../../../../utils/dateUtils'
import {
  saveCashStatementPayablePrefill,
  CASH_STATEMENT_PAYABLE_CREATED_EVENT
} from '../../../../utils/cashStatementPayablePrefill'
import { detectPartialReconciliation, SETTLEMENT_MODES, isApproximateValueMatch, calculateErpTitleRemainingBalance, formatTitleValueLabel, enrichErpTitle } from '../../../../utils/erpTitlePayment'
import { findErpTitleCombinations } from '../../../../utils/reconciliationCombinations'
import { CalendarDatePicker, CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import SelectPersonModal from '../../../../components/ERP/Modal/SelectPersonModal'
import FilterLeftPicker from '../../../../components/ERP/FilterLeftPicker'
import * as XLSX from 'xlsx'
import './ExtratoCaixaPage.css'

function getDefaultPeriodRange() {
  const today = new Date()
  const from = new Date(today)
  from.setDate(from.getDate() - 30)
  return {
    dateFrom: toDateInputValue(from),
    dateTo: toDateInputValue(today)
  }
}

function getTransactionDirection(transaction) {
  return transaction.type || (parseFloat(transaction.amount || 0) >= 0 ? 'credit' : 'debit')
}

function getSignedTransactionAmount(transaction) {
  const amount = Math.abs(parseFloat(transaction.amount || 0))
  const signed = getTransactionDirection(transaction) === 'debit' ? -amount : amount
  return Math.round(signed * 100) / 100
}

function formatReconciledTypeLabel(type) {
  switch (type) {
    case 'account_payable':
      return 'Conta a pagar'
    case 'account_receivable':
      return 'Conta a receber'
    case 'card_reconciliation':
      return 'Conciliação de cartão'
    case 'cash_transfer':
      return 'Transferência'
    default:
      return type || ''
  }
}

function getReconciliationStatusLabel(transaction) {
  if (transaction.reference_type === 'cash_transfer') return 'Transferência'
  if (transaction.reconciled_type) return 'Conciliado'
  return 'Pendente'
}

function getReconciliationReference(transaction) {
  if (transaction.transfer_id) return `#${transaction.transfer_id}`
  if (Array.isArray(transaction.linked_reconciliations) && transaction.linked_reconciliations.length > 0) {
    return transaction.linked_reconciliations
      .map((item) => item?.reconciled_id)
      .filter(Boolean)
      .map((id) => `#${id}`)
      .join(', ')
  }
  if (transaction.reconciled_id) return `#${transaction.reconciled_id}`
  return ''
}

function getReconciliationLinkLabel(transaction) {
  if (transaction.reference_type === 'cash_transfer') return 'Transferência'
  if (Array.isArray(transaction.linked_reconciliations) && transaction.linked_reconciliations.length > 0) {
    return transaction.linked_reconciliations
      .map((item) => formatReconciledTypeLabel(item?.reconciled_type))
      .filter(Boolean)
      .join(', ')
  }
  return formatReconciledTypeLabel(transaction.reconciled_type)
}

function getStatementRowKey(transaction) {
  return `${transaction.source || 'bank_statement'}-${transaction.id}`
}

function canBatchLaunchPayable(transaction, account) {
  if (!account || account.account_type !== 'digital' || !transaction) return false
  if ((transaction.source || 'bank_statement') !== 'bank_statement') return false
  if (transaction.reference_type === 'cash_transfer' || transaction.reconciled_type) return false
  const transactionType = transaction.type || (parseFloat(transaction.amount || 0) >= 0 ? 'credit' : 'debit')
  return transactionType === 'debit'
}

function sanitizeFileNamePart(value) {
  return String(value || 'conta')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40) || 'conta'
}

function applyCurrencyFormat(worksheet, rowIndex, columnIndex) {
  const cell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })]
  if (cell && typeof cell.v === 'number') {
    cell.t = 'n'
    cell.z = '#,##0.00'
  }
}

function exportCashStatementToExcel({
  accountName,
  dateFrom,
  dateTo,
  currentBalance,
  credits,
  debits,
  transactions
}) {
  const periodLabel = `${formatDateOnly(dateFrom)} a ${formatDateOnly(dateTo)}`
  const periodBalance = Math.round((credits - debits) * 100) / 100
  const headerRowIndex = 8

  const rows = [
    ['Extrato de Caixa'],
    ['Conta', accountName || ''],
    ['Período', periodLabel],
    ['Saldo atual', Math.round((Number(currentBalance) || 0) * 100) / 100],
    ['Total de entradas', Math.round(credits * 100) / 100],
    ['Total de saídas', Math.round(debits * 100) / 100],
    ['Saldo do período', periodBalance],
    [],
    ['Data', 'Categoria', 'Lançamento', 'Tipo', 'Valor', 'Conciliação', 'Vínculo', 'Referência', 'Conta contraparte', 'FIT ID', 'Origem']
  ]

  transactions.forEach((transaction) => {
    const direction = getTransactionDirection(transaction)
    rows.push([
      formatDateOnly(transaction.transaction_date),
      transaction.category_name || 'Não Categorizado',
      transaction.description || 'Sem descrição',
      direction === 'credit' ? 'Entrada' : 'Saída',
      getSignedTransactionAmount(transaction),
      getReconciliationStatusLabel(transaction),
      getReconciliationLinkLabel(transaction),
      getReconciliationReference(transaction),
      transaction.counterparty_account_name || '',
      transaction.fit_id || '',
      transaction.source === 'cash_flow' ? 'Caixa' : 'Extrato bancário'
    ])
  })

  rows.push([])
  rows.push(['', '', '', 'Total do período', periodBalance])

  const worksheet = XLSX.utils.aoa_to_sheet(rows)
  ;[3, 4, 5, 6].forEach((rowIndex) => applyCurrencyFormat(worksheet, rowIndex, 1))

  const firstDataRow = headerRowIndex + 1
  transactions.forEach((_, index) => {
    applyCurrencyFormat(worksheet, firstDataRow + index, 4)
  })
  applyCurrencyFormat(worksheet, rows.length - 1, 4)

  worksheet['!cols'] = [
    { wch: 22 },
    { wch: 28 },
    { wch: 48 },
    { wch: 12 },
    { wch: 16 },
    { wch: 16 },
    { wch: 24 },
    { wch: 18 },
    { wch: 28 },
    { wch: 24 },
    { wch: 18 }
  ]
  const lastDataRow = headerRowIndex + transactions.length
  worksheet['!autofilter'] = {
    ref: `A${headerRowIndex + 1}:K${lastDataRow + 1}`
  }

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Extrato')

  const filename = `Extrato_Caixa_${sanitizeFileNamePart(accountName)}_${dateFrom || 'inicio'}_${dateTo || 'fim'}.xlsx`
  XLSX.writeFile(workbook, filename, { bookType: 'xlsx' })
}

function CashStatementPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [accounts, setAccounts] = useState([])
  const [activeAccountId, setActiveAccountId] = useState(null)
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false)
  const [selectedAccountForImport, setSelectedAccountForImport] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isTransferSubmitting, setIsTransferSubmitting] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [linkSearchTerm, setLinkSearchTerm] = useState('')
  const [transactions, setTransactions] = useState([])
  const [destinationTransactions, setDestinationTransactions] = useState([])
  const [sourceTransferTransactions, setSourceTransferTransactions] = useState([])
  const [loadingDestinationTransactions, setLoadingDestinationTransactions] = useState(false)
  const [loadingSourceTransferTransactions, setLoadingSourceTransferTransactions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [transferError, setTransferError] = useState(null)
  const [accountBalance, setAccountBalance] = useState(null)
  const [activeTransaction, setActiveTransaction] = useState(null)
  const [batchSelectedKeys, setBatchSelectedKeys] = useState([])
  const [batchForm, setBatchForm] = useState({
    description: '',
    categoryId: '',
    subcategoryId: '',
    costCenterId: '',
    personType: '',
    personId: '',
    personName: ''
  })
  const [batchCategories, setBatchCategories] = useState([])
  const [batchCostCenters, setBatchCostCenters] = useState([])
  const [batchOptionsLoaded, setBatchOptionsLoaded] = useState(false)
  const [batchSubmitting, setBatchSubmitting] = useState(false)
  const [batchError, setBatchError] = useState(null)
  const [batchPersonModalOpen, setBatchPersonModalOpen] = useState(false)
  const [erpTitles, setErpTitles] = useState([])
  const [loadingTitles, setLoadingTitles] = useState(false)
  const [openDropdownId, setOpenDropdownId] = useState(null)
  const [selectedTitlesToLink, setSelectedTitlesToLink] = useState([]) // Array de títulos selecionados para vincular (antes de confirmar)
  const [linkedReconciliationIds, setLinkedReconciliationIds] = useState([]) // IDs das conciliações já vinculadas
  const [highlightedTransactionKey, setHighlightedTransactionKey] = useState(null)
  const [partialPaymentConfirm, setPartialPaymentConfirm] = useState(null)
  const [boletoFeeConfirm, setBoletoFeeConfirm] = useState(null) // { titles, transactionValue, fees: { [id]: string } }
  const [linkSuccessMessage, setLinkSuccessMessage] = useState(null)
  const [transferForm, setTransferForm] = useState({
    destinationAccountId: '',
    amount: '',
    transferDate: new Date().toISOString().split('T')[0],
    description: ''
  })
  const [digitalTransferSelection, setDigitalTransferSelection] = useState({
    debitTransactionId: '',
    creditTransactionId: ''
  })
  
  // Filtros
  const [typeFilter, setTypeFilter] = useState('all') // 'all' | 'credit' | 'debit'
  const [statusFilter, setStatusFilter] = useState('all') // 'all' | 'reconciled' | 'pending'
  const [amountMin, setAmountMin] = useState('')
  const [amountMax, setAmountMax] = useState('')
  const defaultPeriodRange = useMemo(() => getDefaultPeriodRange(), [])
  const [dateFrom, setDateFrom] = useState(defaultPeriodRange.dateFrom)
  const [dateTo, setDateTo] = useState(defaultPeriodRange.dateTo)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const periodBtnRef = useRef(null)
  
  const dropdownRef = useRef(null)
  const transferDestinationDropdownRef = useRef(null)
  const changeDestinationDropdownRef = useRef(null)
  const fileInputRef = useRef(null)
  const actionDropdownRefs = useRef({})
  const [isTransferDestinationDropdownOpen, setIsTransferDestinationDropdownOpen] = useState(false)
  const [isChangeDestinationModalOpen, setIsChangeDestinationModalOpen] = useState(false)
  const [changeDestinationTransaction, setChangeDestinationTransaction] = useState(null)
  const [changeDestinationAccountId, setChangeDestinationAccountId] = useState('')
  const [isChangeDestinationSubmitting, setIsChangeDestinationSubmitting] = useState(false)
  const [changeDestinationError, setChangeDestinationError] = useState(null)
  const [isChangeDestinationDropdownOpen, setIsChangeDestinationDropdownOpen] = useState(false)
  const [isEditTransferAmountModalOpen, setIsEditTransferAmountModalOpen] = useState(false)
  const [editTransferAmountTransaction, setEditTransferAmountTransaction] = useState(null)
  const [editTransferAmountValue, setEditTransferAmountValue] = useState('')
  const [isEditTransferAmountSubmitting, setIsEditTransferAmountSubmitting] = useState(false)
  const [editTransferAmountError, setEditTransferAmountError] = useState(null)

  const loadAccounts = useCallback(async () => {
    try {
      // Carregar todas as contas (físicas e digitais) para exibição
      // Apenas contas digitais poderão importar OFX
      const response = await financialAccountService.getAll()
      if (response && response.success) {
        const accountsData = response.data || []
        setAccounts(accountsData)
        if (accountsData.length > 0 && !activeAccountId) {
          // Priorizar contas digitais, mas se não houver, selecionar a primeira
          const digitalAccount = accountsData.find(acc => acc.account_type === 'digital')
          const accountToSelect = digitalAccount || accountsData[0]
          const firstAccountId = parseInt(accountToSelect.id, 10)
          if (!isNaN(firstAccountId) && firstAccountId > 0) {
            setActiveAccountId(firstAccountId)
          }
        } else if (accountsData.length === 0) {
          // Se não houver contas, limpar conta ativa
          setActiveAccountId(null)
        }
      }
    } catch (error) {
      console.error('Erro ao carregar contas:', error)
      setError('Erro ao carregar contas financeiras')
    }
  }, [activeAccountId])

  const loadTransactions = useCallback(async () => {
    if (!activeAccountId) return
    
    setLoading(true)
    setError(null)
    try {
      // Garantir que o ID seja um número válido
      const accountId = parseInt(activeAccountId, 10)
      if (isNaN(accountId) || accountId <= 0) {
        setError('ID da conta financeira inválido')
        setLoading(false)
        return
      }

      const filters = {
        financialAccountId: accountId,
        dateFrom,
        dateTo
      }
      
      // Converter filtro de status para booleano (backend pode esperar boolean)
      if (statusFilter === 'reconciled') {
        filters.reconciled = true
      } else if (statusFilter === 'pending') {
        filters.reconciled = false
      }
      // Se statusFilter for 'all', não enviar o filtro reconciled
      
      const response = await cashStatementService.getTransactions(filters)
      if (response && response.success) {
        setTransactions(response.data || [])
      } else {
        const errorMessage = response?.message || response?.error || 'Erro ao carregar transações'
        setError(errorMessage)
        console.error('Erro na resposta do servidor:', response)
      }
    } catch (error) {
      console.error('Erro ao carregar transações:', error)
      
      // Melhorar mensagem de erro baseada no tipo de erro
      let errorMessage = 'Erro ao carregar transações do extrato'
      
      if (error.response) {
        // Erro com resposta do servidor
        const status = error.response.status
        const data = error.response.data
        
        if (status === 500) {
          errorMessage = data?.message || data?.error || 'Erro interno do servidor ao processar transações'
        } else if (status === 404) {
          errorMessage = 'Endpoint de transações não encontrado'
        } else if (status === 400) {
          errorMessage = data?.message || data?.error || 'Parâmetros inválidos na requisição'
        } else {
          errorMessage = data?.message || data?.error || `Erro ${status}: ${error.message}`
        }
      } else if (error.request) {
        // Requisição foi feita mas não houve resposta
        errorMessage = 'Sem resposta do servidor. Verifique sua conexão.'
      } else {
        // Erro ao configurar a requisição
        errorMessage = `Erro ao configurar requisição: ${error.message}`
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [activeAccountId, statusFilter, dateFrom, dateTo])

  const loadBalance = useCallback(async () => {
    if (!activeAccountId) return
    
    try {
      const response = await cashStatementService.getBalance(activeAccountId)
      if (response && response.success) {
        setAccountBalance(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar saldo:', error)
    }
  }, [activeAccountId])

  // Carregar contas financeiras ao montar
  useEffect(() => {
    loadAccounts()
  }, [loadAccounts])

  useEffect(() => {
    const navState = location.state
    const accountIdParam = searchParams.get('accountId')
    const accountId = navState?.financialAccountId || accountIdParam

    if (!accountId) return

    const parsedAccountId = parseInt(accountId, 10)
    if (!isNaN(parsedAccountId) && parsedAccountId > 0) {
      setActiveAccountId(parsedAccountId)
    }
  }, [location.state, searchParams])

  useEffect(() => {
    const highlight = location.state?.highlightTransaction
      || (searchParams.get('highlightId')
        ? {
          id: searchParams.get('highlightId'),
          source: searchParams.get('highlightSource') || 'bank_statement'
        }
        : null)

    if (!highlight || transactions.length === 0) return

    const match = transactions.find((transaction) => (
      String(transaction.id) === String(highlight.id)
      && (transaction.source || 'bank_statement') === highlight.source
    ))

    if (!match) return

    const rowKey = `${match.source || 'bank_statement'}-${match.id}`
    setHighlightedTransactionKey(rowKey)

    requestAnimationFrame(() => {
      document.getElementById(`cash-statement-row-${rowKey}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    })

    if (location.state?.highlightTransaction) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [transactions, location.state, location.pathname, navigate, searchParams])

  // Carregar transações quando a conta ativa mudar
  useEffect(() => {
    if (activeAccountId) {
      loadTransactions()
      loadBalance()
    }
  }, [activeAccountId, typeFilter, statusFilter, dateFrom, dateTo, loadTransactions, loadBalance])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsAccountDropdownOpen(false)
      }
      if (transferDestinationDropdownRef.current && !transferDestinationDropdownRef.current.contains(e.target)) {
        setIsTransferDestinationDropdownOpen(false)
      }
      if (changeDestinationDropdownRef.current && !changeDestinationDropdownRef.current.contains(e.target)) {
        setIsChangeDestinationDropdownOpen(false)
      }
      // Fechar dropdowns de ações
      if (openDropdownId && !e.target.closest('.cash-statement-action-dropdown') && !e.target.closest('.cash-statement-action-button')) {
        setOpenDropdownId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openDropdownId])

  // Atualizar extrato quando conta a pagar for criada e conciliada em outra aba
  useEffect(() => {
    const refreshAfterPayableCreated = (cashStatementTransactionId) => {
      loadTransactions()
      if (
        isLinkModalOpen &&
        activeTransaction &&
        String(activeTransaction.id) === String(cashStatementTransactionId)
      ) {
        setIsLinkModalOpen(false)
        setActiveTransaction(null)
      }
    }

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return
      if (event.data?.type !== CASH_STATEMENT_PAYABLE_CREATED_EVENT) return
      refreshAfterPayableCreated(event.data.cashStatementTransactionId)
    }

    const handleStorage = (event) => {
      if (event.key !== CASH_STATEMENT_PAYABLE_CREATED_EVENT || !event.newValue) return
      try {
        const payload = JSON.parse(event.newValue)
        if (payload?.type === CASH_STATEMENT_PAYABLE_CREATED_EVENT) {
          refreshAfterPayableCreated(payload.cashStatementTransactionId)
        }
      } catch {
        // ignorar payload inválido
      }
    }

    window.addEventListener('message', handleMessage)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('message', handleMessage)
      window.removeEventListener('storage', handleStorage)
    }
  }, [loadTransactions, isLinkModalOpen, activeTransaction])

  useEffect(() => {
    setBatchSelectedKeys([])
    setBatchError(null)
  }, [activeAccountId])


  const activeAccount = useMemo(() => {
    return accounts.find(a => a.id === activeAccountId) || null
  }, [accounts, activeAccountId])

  const selectedDestinationAccount = useMemo(() => {
    const destinationId = parseInt(transferForm.destinationAccountId, 10)
    return accounts.find(a => parseInt(a.id, 10) === destinationId) || null
  }, [accounts, transferForm.destinationAccountId])

  const transferMode = useMemo(() => {
    if (!activeAccount || !selectedDestinationAccount) return null
    return `${activeAccount.account_type}-${selectedDestinationAccount.account_type}`
  }, [activeAccount, selectedDestinationAccount])

  const transferDestinationAccounts = useMemo(() => {
    const sourceId = parseInt(activeAccountId, 10)
    return accounts.filter(acc => parseInt(acc.id, 10) !== sourceId)
  }, [accounts, activeAccountId])

  const changeDestinationAccounts = useMemo(() => {
    const sourceId = parseInt(changeDestinationTransaction?.transfer_source_account_id || activeAccountId, 10)
    return accounts.filter(acc => parseInt(acc.id, 10) !== sourceId)
  }, [accounts, changeDestinationTransaction, activeAccountId])

  const selectedChangeDestinationAccount = useMemo(() => {
    const destinationId = parseInt(changeDestinationAccountId, 10)
    return accounts.find(a => parseInt(a.id, 10) === destinationId) || null
  }, [accounts, changeDestinationAccountId])

  const sourceDigitalDebitCandidates = useMemo(() => {
    return sourceTransferTransactions.filter(transaction => {
      const transactionType = transaction.type || (parseFloat(transaction.amount || 0) >= 0 ? 'credit' : 'debit')
      return transaction.source === 'bank_statement' &&
        transactionType === 'debit' &&
        !transaction.reconciled_type
    })
  }, [sourceTransferTransactions])

  const destinationDigitalCreditCandidates = useMemo(() => {
    const targetAmount = parseFloat(transferForm.amount || 0)
    return destinationTransactions.filter(transaction => {
      const transactionType = transaction.type || (parseFloat(transaction.amount || 0) >= 0 ? 'credit' : 'debit')
      const amountMatches = !targetAmount || Math.abs(Math.abs(parseFloat(transaction.amount || 0)) - targetAmount) < 0.01
      return transaction.source === 'bank_statement' &&
        transactionType === 'credit' &&
        !transaction.reconciled_type &&
        amountMatches
    })
  }, [destinationTransactions, transferForm.amount])

  useEffect(() => {
    const needsSourceDebits = isTransferModalOpen && (transferMode === 'digital-digital' || transferMode === 'digital-physical') && activeAccount
    if (!needsSourceDebits) {
      setSourceTransferTransactions([])
      return undefined
    }

    let cancelled = false
    const loadSourceDebits = async () => {
      setLoadingSourceTransferTransactions(true)
      try {
        const response = await cashStatementService.getTransactions({
          financialAccountId: activeAccount.id,
          reconciled: false
        })
        if (!cancelled) {
          setSourceTransferTransactions(response?.success ? (response.data || []) : [])
        }
      } catch (loadError) {
        console.error('Erro ao carregar débitos da conta de origem:', loadError)
        if (!cancelled) setSourceTransferTransactions([])
      } finally {
        if (!cancelled) setLoadingSourceTransferTransactions(false)
      }
    }

    loadSourceDebits()
    return () => {
      cancelled = true
    }
  }, [isTransferModalOpen, transferMode, activeAccount])

  useEffect(() => {
    const loadDestinationTransactions = async () => {
      if (!isTransferModalOpen || transferMode !== 'digital-digital' || !selectedDestinationAccount) {
        setDestinationTransactions([])
        return
      }

      setLoadingDestinationTransactions(true)
      try {
        const response = await cashStatementService.getTransactions({
          financialAccountId: selectedDestinationAccount.id,
          reconciled: false
        })
        if (response && response.success) {
          setDestinationTransactions(response.data || [])
        } else {
          setDestinationTransactions([])
        }
      } catch (error) {
        console.error('Erro ao carregar lançamentos da conta destino:', error)
        setDestinationTransactions([])
      } finally {
        setLoadingDestinationTransactions(false)
      }
    }

    loadDestinationTransactions()
  }, [isTransferModalOpen, transferMode, selectedDestinationAccount])

  const handleImportOFX = async () => {
    if (!selectedAccountForImport || !fileInputRef.current?.files?.[0]) return
    
    setIsImporting(true)
    setError(null)
    
    try {
      // Garantir que o ID seja um número válido
      const accountId = parseInt(selectedAccountForImport, 10)
      if (isNaN(accountId) || accountId <= 0) {
        setError('ID da conta financeira inválido para importação')
        setIsImporting(false)
        return
      }

      const file = fileInputRef.current.files[0]
      const response = await cashStatementService.uploadOfx(file, accountId)
      
      if (response && response.success) {
        // Recarregar transações e saldo
        await Promise.all([
          loadTransactions(), 
          loadBalance()
        ])
        setIsModalOpen(false)
        setSelectedAccountForImport(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      } else {
        setError(response?.message || 'Erro ao importar arquivo OFX')
      }
    } catch (error) {
      console.error('Erro ao importar OFX:', error)
      setError(error.response?.data?.message || 'Erro ao importar arquivo OFX')
    } finally {
      setIsImporting(false)
    }
  }

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImportOFX()
    }
  }

  const handleOpenTransferModal = () => {
    const defaultDestination = accounts.find(acc => parseInt(acc.id, 10) !== parseInt(activeAccountId, 10))
    setTransferForm({
      destinationAccountId: defaultDestination ? String(defaultDestination.id) : '',
      amount: '',
      transferDate: new Date().toISOString().split('T')[0],
      description: ''
    })
    setDigitalTransferSelection({
      debitTransactionId: '',
      creditTransactionId: ''
    })
    setDestinationTransactions([])
    setTransferError(null)
    setIsTransferDestinationDropdownOpen(false)
    setIsTransferModalOpen(true)
  }

  const handleTransferFormChange = (field, value) => {
    setTransferForm(prev => ({
      ...prev,
      [field]: value
    }))

    if (field === 'destinationAccountId') {
      setDigitalTransferSelection({
        debitTransactionId: '',
        creditTransactionId: ''
      })
    }
  }

  const handleSubmitTransfer = async () => {
    if (!activeAccount || !selectedDestinationAccount) {
      setTransferError('Selecione a conta de destino')
      return
    }

    setIsTransferSubmitting(true)
    setTransferError(null)

    try {
      let response

      if (transferMode === 'digital-digital') {
        if (!digitalTransferSelection.debitTransactionId || !digitalTransferSelection.creditTransactionId) {
          setTransferError('Selecione o débito da origem e o crédito da conta destino')
          setIsTransferSubmitting(false)
          return
        }

        response = await cashStatementService.linkDigitalTransfer({
          debitTransactionId: digitalTransferSelection.debitTransactionId,
          creditTransactionId: digitalTransferSelection.creditTransactionId,
          transferDate: transferForm.transferDate,
          description: transferForm.description
        })
      } else if (transferMode === 'digital-physical') {
        if (!digitalTransferSelection.debitTransactionId) {
          setTransferError('Selecione o débito OFX da conta digital')
          setIsTransferSubmitting(false)
          return
        }

        response = await cashStatementService.createTransfer({
          sourceAccountId: activeAccount.id,
          destinationAccountId: selectedDestinationAccount.id,
          amount: transferForm.amount,
          transferDate: transferForm.transferDate,
          description: transferForm.description,
          debitTransactionId: digitalTransferSelection.debitTransactionId
        })
      } else {
        response = await cashStatementService.createTransfer({
          sourceAccountId: activeAccount.id,
          destinationAccountId: selectedDestinationAccount.id,
          amount: transferForm.amount,
          transferDate: transferForm.transferDate,
          description: transferForm.description
        })
      }

      if (response && response.success) {
        await Promise.all([
          loadTransactions(),
          loadBalance()
        ])
        setIsTransferModalOpen(false)
      } else {
        setTransferError(response?.message || 'Erro ao registrar transferência')
      }
    } catch (error) {
      console.error('Erro ao registrar transferência:', error)
      setTransferError(error.response?.data?.message || 'Erro ao registrar transferência')
    } finally {
      setIsTransferSubmitting(false)
    }
  }

  const handleOpenChangeDestinationModal = (transaction) => {
    setChangeDestinationTransaction(transaction)
    setChangeDestinationAccountId(
      transaction.transfer_destination_account_id
        ? String(transaction.transfer_destination_account_id)
        : ''
    )
    setChangeDestinationError(null)
    setIsChangeDestinationDropdownOpen(false)
    setIsChangeDestinationModalOpen(true)
  }

  const handleOpenEditTransferAmountModal = (transaction) => {
    setEditTransferAmountTransaction(transaction)
    setEditTransferAmountValue(
      String(Math.abs(parseFloat(transaction.amount || 0)).toFixed(2))
    )
    setEditTransferAmountError(null)
    setIsEditTransferAmountModalOpen(true)
  }

  const handleSubmitEditTransferAmount = async () => {
    if (!editTransferAmountTransaction?.transfer_id) {
      setEditTransferAmountError('Transferência inválida')
      return
    }

    const parsedAmount = parseFloat(editTransferAmountValue)
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setEditTransferAmountError('Informe um valor válido maior que zero')
      return
    }

    const currentAmount = Math.abs(parseFloat(editTransferAmountTransaction.amount || 0))
    if (Math.abs(parsedAmount - currentAmount) < 0.01) {
      setEditTransferAmountError('Informe um valor diferente do atual')
      return
    }

    setIsEditTransferAmountSubmitting(true)
    setEditTransferAmountError(null)

    try {
      const response = await cashStatementService.updateTransferAmount(
        editTransferAmountTransaction.transfer_id,
        parsedAmount
      )

      if (response && response.success) {
        await Promise.all([
          loadTransactions(),
          loadBalance()
        ])
        setIsEditTransferAmountModalOpen(false)
        setEditTransferAmountTransaction(null)
      } else {
        setEditTransferAmountError(response?.message || 'Erro ao alterar valor da transferência')
      }
    } catch (error) {
      console.error('Erro ao alterar valor da transferência:', error)
      setEditTransferAmountError(error.response?.data?.message || 'Erro ao alterar valor da transferência')
    } finally {
      setIsEditTransferAmountSubmitting(false)
    }
  }

  const handleSubmitChangeDestination = async () => {
    if (!changeDestinationTransaction?.transfer_id || !selectedChangeDestinationAccount) {
      setChangeDestinationError('Selecione a nova conta de destino')
      return
    }

    const currentDestinationId = parseInt(changeDestinationTransaction.transfer_destination_account_id, 10)
    const newDestinationId = parseInt(selectedChangeDestinationAccount.id, 10)

    if (currentDestinationId === newDestinationId) {
      setChangeDestinationError('Selecione uma conta destino diferente da atual')
      return
    }

    setIsChangeDestinationSubmitting(true)
    setChangeDestinationError(null)

    try {
      const response = await cashStatementService.updateTransferDestination(
        changeDestinationTransaction.transfer_id,
        newDestinationId
      )

      if (response && response.success) {
        await Promise.all([
          loadTransactions(),
          loadBalance()
        ])
        setIsChangeDestinationModalOpen(false)
        setChangeDestinationTransaction(null)
      } else {
        setChangeDestinationError(response?.message || 'Erro ao alterar conta destino')
      }
    } catch (error) {
      console.error('Erro ao alterar conta destino:', error)
      setChangeDestinationError(error.response?.data?.message || 'Erro ao alterar conta destino')
    } finally {
      setIsChangeDestinationSubmitting(false)
    }
  }

  const getTransactionType = (transaction) => {
    if (!transaction) return 'credit'
    return transaction.type || (parseFloat(transaction.amount || 0) >= 0 ? 'credit' : 'debit')
  }

  const handleOpenLinkModal = (transaction) => {
    setActiveTransaction(transaction)
    setIsLinkModalOpen(true)
    setLinkSearchTerm('')
    setErpTitles([])
    setSelectedTitlesToLink([])
    setPartialPaymentConfirm(null)
    setLinkSuccessMessage(null)
    
    // Extrair IDs das conciliações já vinculadas
    if (transaction.linked_reconciliations) {
      const linkedIds = transaction.linked_reconciliations
        .filter(r => r.reconciled_type === 'card_reconciliation')
        .map(r => String(r.reconciled_id))
      setLinkedReconciliationIds(linkedIds)
    } else {
      setLinkedReconciliationIds([])
    }
  }

  const buildPayablePrefillPayload = () => {
    if (!activeTransaction || getTransactionType(activeTransaction) !== 'debit') return null

    const transactionDate = toDateInputValue(activeTransaction.transaction_date)
    const amount = Math.abs(parseFloat(activeTransaction.amount || 0))

    return {
      fromCashStatement: true,
      prefill: {
        description: activeTransaction.description || '',
        amount: amount > 0 ? String(amount) : '',
        firstDueDate: transactionDate,
        competenceDate: transactionDate,
        financialAccountId: activeAccountId || '',
        observations: `Origem: extrato bancário (lançamento #${activeTransaction.id})`,
        cashStatementTransactionId: activeTransaction.id
      }
    }
  }

  const handleNavigateToNewPayable = (event) => {
    event?.stopPropagation?.()

    const payload = buildPayablePrefillPayload()
    if (!payload) {
      event?.preventDefault?.()
      return
    }

    saveCashStatementPayablePrefill(payload)
  }

  const handleToggleTitleSelection = (title) => {
    // Verificar se já está vinculada
    const isAlreadyLinked = title.erpType === 'card_reconciliation' && 
      linkedReconciliationIds.includes(String(title.id))
    
    if (isAlreadyLinked) {
      return // Não permitir selecionar se já está vinculada
    }
    
    // Toggle da seleção
    setSelectedTitlesToLink(prev => {
      const isSelected = prev.some(t => t.id === title.id)
      if (isSelected) {
        // Remover da seleção
        return prev.filter(t => t.id !== title.id)
      } else {
        // Adicionar à seleção
        return [...prev, title]
      }
    })
  }

  const isBoletoReceivable = (title) =>
    title?.erpType === 'account_receivable' && Boolean(title?.hasBoleto || title?.latestBoleto)

  const openBoletoFeeConfirm = (titles, transactionValue) => {
    setError(null)
    const fees = {}
    titles.forEach((t) => {
      const remaining = calculateErpTitleRemainingBalance(t)
      const knownFee = t.latestBoleto?.fee_amount != null ? Number(t.latestBoleto.fee_amount) : null
      if (knownFee != null && !Number.isNaN(knownFee)) {
        fees[t.id] = String(knownFee)
      } else if (titles.length === 1 && transactionValue + 0.01 < remaining) {
        // Único título: sugere taxa = saldo − crédito do extrato
        fees[t.id] = (remaining - transactionValue).toFixed(2)
      } else {
        fees[t.id] = ''
      }
    })
    setBoletoFeeConfirm({ titles, transactionValue, fees, localError: null })
  }

  const handleConfirmBoletoFeeLink = async () => {
    if (!boletoFeeConfirm) return
    const { titles, transactionValue, fees } = boletoFeeConfirm
    const AMOUNT_TOLERANCE = 0.01

    let netSum = 0
    for (const title of titles) {
      const remaining = calculateErpTitleRemainingBalance(title)
      const fee = parseFloat(String(fees[title.id] ?? '').replace(',', '.')) || 0
      if (fee < 0) {
        setBoletoFeeConfirm((prev) => (prev ? { ...prev, localError: 'Taxa não pode ser negativa' } : prev))
        return
      }
      if (fee + AMOUNT_TOLERANCE >= remaining) {
        setBoletoFeeConfirm((prev) =>
          prev
            ? {
                ...prev,
                localError: `Taxa do título #${title.id} deve ser menor que o saldo`
              }
            : prev
        )
        return
      }
      netSum += remaining - fee
    }

    if (Math.abs(netSum - transactionValue) > AMOUNT_TOLERANCE) {
      setBoletoFeeConfirm((prev) =>
        prev
          ? {
              ...prev,
              localError: `Soma dos líquidos deve ser igual ao extrato (${netSum.toFixed(2)} ≠ ${transactionValue.toFixed(2)})`
            }
          : prev
      )
      return
    }

    await handleConfirmLink(titles, SETTLEMENT_MODES.FULL_WITH_FEE_EXPENSE, fees)
  }

  const handleConfirmLink = async (titlesToLink = null, settlementMode = null, feeByTitleId = null) => {
    if (!activeTransaction) return
    
    // Usar títulos passados como parâmetro ou os selecionados
    const titles = titlesToLink || selectedTitlesToLink
    
    if (titles.length === 0) {
      setError('Selecione pelo menos uma conciliação para vincular')
      return
    }

    const transactionValue = Math.abs(parseFloat(activeTransaction.amount || 0))
    const allBoletoReceivables = titles.every(isBoletoReceivable)

    // Origem boleto: pedir taxas e quitar face cheia + despesa (antes do fluxo parcial/abatimento)
    if (!settlementMode && allBoletoReceivables) {
      openBoletoFeeConfirm(titles, transactionValue)
      return
    }

    if (!settlementMode && titles.length === 1) {
      const partialInfo = detectPartialReconciliation(transactionValue, titles[0])
      if (partialInfo) {
        setPartialPaymentConfirm({ titles, ...partialInfo })
        return
      }
    }

    const resolvedSettlementMode = settlementMode || SETTLEMENT_MODES.PARTIAL
    
    try {
      setError(null)
      setLinkSuccessMessage(null)
      setPartialPaymentConfirm(null)
      setBoletoFeeConfirm(null)

      const responses = []
      for (const erpTitle of titles) {
        if (erpTitle.erpType === 'cash_transfer') {
          responses.push(
            await cashStatementService.linkTransferBankTransaction(
              erpTitle.id,
              activeTransaction.id
            )
          )
          continue
        }

        const transactionType = activeTransaction.type || (parseFloat(activeTransaction.amount || 0) >= 0 ? 'credit' : 'debit')
        const erpTitleType = erpTitle.erpType || (transactionType === 'credit' ? 'account_receivable' : 'account_payable')
        const feeAmount =
          resolvedSettlementMode === SETTLEMENT_MODES.FULL_WITH_FEE_EXPENSE
            ? Number(feeByTitleId?.[erpTitle.id] ?? 0)
            : null

        responses.push(
          await cashStatementService.linkTransaction(
            activeTransaction.id,
            erpTitle.id,
            erpTitleType,
            resolvedSettlementMode,
            feeAmount
          )
        )
      }
      
      const allSuccess = responses.every(r => r && r.success)
      
      if (allSuccess) {
        const successMessages = responses
          .map((response) => response?.message)
          .filter(Boolean)
        if (successMessages.length > 0) {
          setLinkSuccessMessage(successMessages.join(' '))
        }

        await loadTransactions()
        
        const updatedTransactions = await cashStatementService.getTransactions({
          financialAccountId: activeAccountId,
          dateFrom,
          dateTo
        })
        
        if (updatedTransactions && updatedTransactions.success) {
          const updatedTransaction = updatedTransactions.data.find(t => t.id === activeTransaction.id)
          if (updatedTransaction) {
            setActiveTransaction(updatedTransaction)
            if (updatedTransaction.linked_reconciliations) {
              const linkedIds = updatedTransaction.linked_reconciliations
                .filter(r => r.reconciled_type === 'card_reconciliation')
                .map(r => String(r.reconciled_id))
              setLinkedReconciliationIds(linkedIds)
            }
          }
        }
        
        setLinkSearchTerm('')
        setErpTitles([])
        setSelectedTitlesToLink([])
      } else {
        const errorMessages = responses
          .filter(r => !r || !r.success)
          .map(r => r?.message || 'Erro ao vincular transação')
        setError(errorMessages.join('; '))
      }
    } catch (error) {
      console.error('Erro ao vincular transação:', error)
      setError(error.response?.data?.message || 'Erro ao vincular transação')
    }
  }

  // Buscar títulos do ERP e conciliações de cartão para vinculação
  useEffect(() => {
    if (!isLinkModalOpen || !activeTransaction) return

    const searchErpTitles = async () => {
      setLoadingTitles(true)
      try {
        const transactionType = activeTransaction.type || (parseFloat(activeTransaction.amount || 0) >= 0 ? 'credit' : 'debit')
        const isCredit = transactionType === 'credit'
        const transactionValue = Math.abs(parseFloat(activeTransaction.amount || 0))
        
        // Buscar em contas a receber (créditos) ou contas a pagar (débitos)
        const service = isCredit ? accountReceivableService : accountPayableService
        
        // Se há termo de busca, usar ele; senão, buscar contas pendentes para permitir seleção múltipla
        const searchParams = linkSearchTerm.trim() 
          ? { search: linkSearchTerm, limit: 50 } // Aumentar limite para busca manual
          : { status: ['pending', 'overdue'], limit: 100 } // Buscar contas pendentes/vencidas quando não há busca
        
        const [titlesResponse, cardReconciliationsResponse, pendingTransfersResponse] = await Promise.all([
          service.getAll(searchParams),
          // Buscar conciliações de cartão pendentes apenas para créditos
          isCredit ? cardReconciliationService.getAll({ status: 'pending', limit: 50 }) : Promise.resolve({ success: false, data: [] }),
          isCredit ? cashStatementService.getPendingTransfers({
            destinationAccountId: activeAccountId,
            amount: transactionValue
          }) : Promise.resolve({ success: false, data: [] })
        ])

        let allTitles = []

        // Processar contas a pagar/receber
        if (titlesResponse && titlesResponse.success) {
          const titles = (titlesResponse.data || []).map(title => {
            const isPayable = !isCredit
            const entity = isCredit
              ? (title.customer_name || title.user_name || title.entity_name || 'N/A')
              : (title.supplier_name || title.customer_name || title.user_name || title.entity_name || 'N/A')

            return enrichErpTitle({
              id: title.id?.toString() || String(title.id),
              description: title.description || title.notes || title.observation || 'Sem descrição',
              value: parseFloat(title.amount || title.value || 0),
              amount: parseFloat(title.amount || title.value || 0),
              paidAmount: parseFloat(title.paid_amount || 0),
              paid_amount: parseFloat(title.paid_amount || 0),
              discountAmount: parseFloat(title.discount_amount || 0),
              discount_amount: parseFloat(title.discount_amount || 0),
              interestAmount: parseFloat(title.interest_amount || 0),
              interest_amount: parseFloat(title.interest_amount || 0),
              fineAmount: parseFloat(title.fine_amount || 0),
              fine_amount: parseFloat(title.fine_amount || 0),
              status: title.status,
              date: title.due_date || title.date || title.created_at,
              entity,
              customer_id: title.customer_id ?? null,
              supplier_id: title.supplier_id ?? null,
              user_id: title.user_id ?? null,
              customer_name: title.customer_name || null,
              supplier_name: title.supplier_name || null,
              user_name: title.user_name || null,
              type: isCredit ? 'C' : 'D',
              erpType: isPayable ? 'account_payable' : 'account_receivable',
              latestBoleto: title.latest_boleto || null,
              hasBoleto: Boolean(title.latest_boleto)
            })
          })
          allTitles.push(...titles)
        }

        // Processar conciliações de cartão (apenas para créditos)
        if (isCredit && cardReconciliationsResponse && cardReconciliationsResponse.success) {
          const reconciliations = (cardReconciliationsResponse.data || []).map(reconciliation => {
            // Usar system_net_amount primeiro (valor que não muda ao desvincular)
            // bank_statement_net_amount pode ser 0 após desvincular, então não usar como prioridade
            const value = parseFloat(
              reconciliation.system_net_amount || 
              reconciliation.expected_amount || 
              reconciliation.actual_amount || 
              reconciliation.bank_statement_net_amount || 
              0
            )
            
            return {
              id: reconciliation.id?.toString() || String(reconciliation.id),
              description: reconciliation.observation || `Conciliação de cartão - ${reconciliation.payment_receipt_method_name || 'N/A'}`,
              value: value,
              date: reconciliation.reconciliation_date || reconciliation.created_at,
              entity: reconciliation.payment_receipt_method_name || reconciliation.payment_method_name || 'Cartão',
              type: 'C',
              erpType: 'card_reconciliation',
              reconciliationDate: reconciliation.reconciliation_date,
              paymentMethod: reconciliation.payment_receipt_method_name || reconciliation.payment_method_name,
              fitId: reconciliation.bank_statement_fit_id || null // Incluir fit_id para busca
            }
          })
          allTitles.push(...reconciliations)
        }

        // Processar transferências físicas-digitais pendentes (apenas créditos digitais)
        if (isCredit && pendingTransfersResponse && pendingTransfersResponse.success) {
          const transfers = (pendingTransfersResponse.data || []).map(transfer => ({
            id: transfer.id?.toString() || String(transfer.id),
            description: transfer.description || 'Transferência entre contas',
            value: parseFloat(transfer.amount || 0),
            date: transfer.transfer_date || transfer.created_at,
            entity: `Transferência de ${transfer.source_account_name || 'conta física'}`,
            type: 'C',
            erpType: 'cash_transfer',
            destinationAccount: transfer.destination_account_name
          }))
          allTitles.push(...transfers)
        }
        
        // Se não há termo de busca, mostrar contas pendentes que podem ser combinadas
        // Incluir todas as contas pendentes para permitir seleção múltipla
        if (!linkSearchTerm.trim()) {
          // Filtrar apenas contas pendentes/vencidas (não pagas)
          const pendingTitles = allTitles.filter(title => {
            // Para contas a receber/pagar, verificar se está pendente
            if (title.erpType === 'account_receivable' || title.erpType === 'account_payable') {
              // Incluir todas as contas pendentes, independente do valor
              // O usuário pode selecionar múltiplas para somar o valor total
              return true
            }
            // Para conciliações de cartão, manter filtro por valor
            if (title.erpType === 'card_reconciliation') {
              const valueDiff = Math.abs(Math.abs(title.value) - transactionValue)
              const tolerance = Math.max(title.value * 0.01, 5.00) // 1% ou R$ 5,00
              return valueDiff <= tolerance
            }
            return true
          })
          setErpTitles(pendingTitles)
        } else {
          // Se há termo de busca, incluir todos os resultados (permitir busca por ID mesmo que valor seja diferente)
          setErpTitles(allTitles)
        }
      } catch (error) {
        console.error('Erro ao buscar títulos:', error)
        setErpTitles([])
      } finally {
        setLoadingTitles(false)
      }
    }

    // Buscar imediatamente ao abrir o modal, depois usar debounce para busca manual
    if (!linkSearchTerm.trim()) {
      searchErpTitles()
    } else {
      const debounceTimer = setTimeout(searchErpTitles, 300)
      return () => clearTimeout(debounceTimer)
    }
  }, [linkSearchTerm, isLinkModalOpen, activeTransaction, activeAccountId])

  // Sugestões inteligentes baseadas em valor e tipo
  const erpSuggestions = useMemo(() => {
    if (!activeTransaction) return []
    
    const transactionType = activeTransaction.type || (parseFloat(activeTransaction.amount || 0) >= 0 ? 'credit' : 'debit')
    const transactionValue = Math.abs(parseFloat(activeTransaction.amount || 0))
    
    // Filtrar títulos que correspondem ao valor e tipo
    return erpTitles.filter(title => {
      const remainingBalance = calculateErpTitleRemainingBalance(title)
      const valueMatch = Math.abs(remainingBalance - transactionValue) < 0.01
      const typeMatch = title.type === (transactionType === 'credit' ? 'C' : 'D')
      return valueMatch && typeMatch
    })
  }, [activeTransaction, erpTitles])

  // Títulos com saldo pendente próximo ao valor do extrato (mas não idêntico)
  const erpApproximateMatches = useMemo(() => {
    if (!activeTransaction) return []

    const transactionType = activeTransaction.type || (parseFloat(activeTransaction.amount || 0) >= 0 ? 'credit' : 'debit')
    const transactionValue = Math.abs(parseFloat(activeTransaction.amount || 0))

    return erpTitles
      .filter(title => {
        const typeMatch = title.type === (transactionType === 'credit' ? 'C' : 'D')
        return typeMatch && isApproximateValueMatch(transactionValue, title)
      })
      .sort((a, b) => {
        const diffA = Math.abs(calculateErpTitleRemainingBalance(a) - transactionValue)
        const diffB = Math.abs(calculateErpTitleRemainingBalance(b) - transactionValue)
        return diffA - diffB
      })
      .slice(0, 10)
  }, [activeTransaction, erpTitles])

  // Combinações: mesma parte (fornecedor/cliente/usuário) + soma ≈ valor do extrato
  const erpCombinations = useMemo(() => {
    if (!activeTransaction || erpTitles.length === 0) return []

    const transactionType = activeTransaction.type || (parseFloat(activeTransaction.amount || 0) >= 0 ? 'credit' : 'debit')
    const transactionValue = Math.abs(parseFloat(activeTransaction.amount || 0))

    const pendingTitles = erpTitles.filter(title => {
      const typeMatch = title.type === (transactionType === 'credit' ? 'C' : 'D')
      return typeMatch && (title.erpType === 'account_receivable' || title.erpType === 'account_payable')
    })

    return findErpTitleCombinations(pendingTitles, transactionValue, {
      maxResults: 5,
      maxSize: 4,
    }).map((combo) => combo.titles)
  }, [activeTransaction, erpTitles])

  // Resultados da busca manual
  const erpSearchResults = useMemo(() => {
    if (!linkSearchTerm || !activeTransaction) return []
    
    const transactionType = activeTransaction.type || (parseFloat(activeTransaction.amount || 0) >= 0 ? 'credit' : 'debit')
    const searchLower = linkSearchTerm.toLowerCase()
    const searchTermTrimmed = linkSearchTerm.trim()
    
    // Verificar se o termo de busca é um ID numérico (permitir busca por "17", "18", etc)
    const isNumericId = /^\d+$/.test(searchTermTrimmed)
    
    // Verificar se o termo de busca é um fit_id (código da transação)
    const isFitIdSearch = searchTermTrimmed.length > 10 && /^[A-Za-z0-9_-]+$/.test(searchTermTrimmed)
    
    return erpTitles.filter(title => {
      const typeMatch = title.type === (transactionType === 'credit' ? 'C' : 'D')
      
      // Se for busca por ID numérico, buscar exatamente pelo ID
      if (isNumericId) {
        const searchMatch = title.id === searchTermTrimmed || title.id.toString() === searchTermTrimmed
        return typeMatch && searchMatch
      }
      
      // Se for busca por fit_id, permitir mesmo que valor seja diferente
      if (isFitIdSearch) {
        // Buscar por ID da conciliação ou qualquer campo que contenha o termo
        const searchMatch = 
          title.id.toLowerCase().includes(searchLower) ||
          title.description.toLowerCase().includes(searchLower) ||
          title.entity.toLowerCase().includes(searchLower) ||
          (title.fitId && title.fitId.toLowerCase().includes(searchLower))
        return typeMatch && searchMatch
      }
      
      // Busca normal: por descrição, ID ou entidade
      const searchMatch = 
        title.description.toLowerCase().includes(searchLower) ||
        title.id.toLowerCase().includes(searchLower) ||
        title.id.toString().includes(searchTermTrimmed) || // Buscar também como string
        title.entity.toLowerCase().includes(searchLower)
      return typeMatch && searchMatch
    })
  }, [linkSearchTerm, activeTransaction, erpTitles])

  const filteredTransactions = useMemo(() => {
    let filtered = transactions

    // Filtro de busca
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(t => 
        (t.description || '').toLowerCase().includes(searchLower) ||
        (t.fit_id || '').toLowerCase().includes(searchLower) ||
        (t.counterparty_account_name || '').toLowerCase().includes(searchLower)
      )
    }

    // Filtro de tipo (credit/debit baseado no campo type, com fallback para sinal do amount)
    if (typeFilter === 'credit') {
      filtered = filtered.filter(t => {
        const transactionType = t.type || (parseFloat(t.amount || 0) >= 0 ? 'credit' : 'debit')
        return transactionType === 'credit'
      })
    } else if (typeFilter === 'debit') {
      filtered = filtered.filter(t => {
        const transactionType = t.type || (parseFloat(t.amount || 0) >= 0 ? 'credit' : 'debit')
        return transactionType === 'debit'
      })
    }

    // Filtro de status de conciliação
    if (statusFilter === 'reconciled') {
      filtered = filtered.filter(t => t.reconciled_type !== null)
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(t => t.reconciled_type === null)
    }

    // Filtro por intervalo de valor (valor absoluto do lançamento)
    const minValue = amountMin !== '' ? parseFloat(amountMin) : null
    const maxValue = amountMax !== '' ? parseFloat(amountMax) : null
    if (minValue !== null && Number.isFinite(minValue)) {
      filtered = filtered.filter(t => Math.abs(parseFloat(t.amount || 0)) >= minValue)
    }
    if (maxValue !== null && Number.isFinite(maxValue)) {
      filtered = filtered.filter(t => Math.abs(parseFloat(t.amount || 0)) <= maxValue)
    }

    return filtered
  }, [transactions, searchTerm, typeFilter, statusFilter, amountMin, amountMax])

  const summary = useMemo(() => {
    // Usar o campo 'type' da transação, com fallback para o sinal do amount
    const credits = filteredTransactions
      .filter(t => {
        const transactionType = t.type || (parseFloat(t.amount || 0) >= 0 ? 'credit' : 'debit')
        return transactionType === 'credit'
      })
      .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || 0)), 0)
    
    const debits = filteredTransactions
      .filter(t => {
        const transactionType = t.type || (parseFloat(t.amount || 0) >= 0 ? 'credit' : 'debit')
        return transactionType === 'debit'
      })
      .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount || 0)), 0)
    
    return { credits, debits }
  }, [filteredTransactions])

  const isDigitalAccount = activeAccount?.account_type === 'digital'

  const batchSelectedTransactions = useMemo(() => {
    if (!isDigitalAccount) return []
    const selected = new Set(batchSelectedKeys)
    return filteredTransactions.filter((transaction) => (
      selected.has(getStatementRowKey(transaction)) && canBatchLaunchPayable(transaction, activeAccount)
    ))
  }, [activeAccount, batchSelectedKeys, filteredTransactions, isDigitalAccount])

  const batchTotal = useMemo(() => (
    batchSelectedTransactions.reduce((sum, transaction) => sum + Math.abs(parseFloat(transaction.amount || 0)), 0)
  ), [batchSelectedTransactions])

  useEffect(() => {
    if (batchSelectedTransactions.length < 2 || batchOptionsLoaded) return

    let cancelled = false
    const loadBatchOptions = async () => {
      try {
        const [categoriesResponse, costCentersResponse] = await Promise.all([
          financialCategoryService.getAll({ type: 'expense', active: true }),
          costCenterService.getAll({ active: true })
        ])
        if (cancelled) return
        if (categoriesResponse?.success) setBatchCategories(categoriesResponse.data || [])
        if (costCentersResponse?.success) setBatchCostCenters(costCentersResponse.data || [])
        setBatchOptionsLoaded(true)
      } catch (loadError) {
        if (!cancelled) setBatchError('Não foi possível carregar categorias e centros de custo')
      }
    }

    loadBatchOptions()
    return () => {
      cancelled = true
    }
  }, [batchOptionsLoaded, batchSelectedTransactions.length])

  useEffect(() => {
    if (batchSelectedTransactions.length < 2) return
    const descriptions = [...new Set(
      batchSelectedTransactions.map((transaction) => (transaction.description || '').trim())
    )]
    if (descriptions.length !== 1 || !descriptions[0]) return
    setBatchForm((prev) => (
      prev.description === descriptions[0] ? prev : { ...prev, description: descriptions[0] }
    ))
  }, [batchSelectedTransactions])

  const toggleBatchSelection = (transaction) => {
    const key = getStatementRowKey(transaction)
    setBatchError(null)
    setBatchSelectedKeys((prev) => (
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    ))
  }

  const clearBatchSelection = () => {
    setBatchSelectedKeys([])
    setBatchError(null)
    setBatchForm({
      description: '',
      categoryId: '',
      subcategoryId: '',
      costCenterId: '',
      personType: '',
      personId: '',
      personName: ''
    })
  }

  const handleBatchLaunchPayables = async () => {
    if (batchSelectedTransactions.length < 2 || batchSubmitting) return

    if (!batchForm.personId || !batchForm.personType) {
      setBatchError('Selecione a pessoa do lançamento')
      return
    }
    if (!batchForm.description.trim()) {
      setBatchError('Informe a descrição usada em todos os lançamentos')
      return
    }
    if (!batchForm.categoryId) {
      setBatchError('Selecione a categoria')
      return
    }

    setBatchSubmitting(true)
    setBatchError(null)
    const failures = []

    for (const transaction of batchSelectedTransactions) {
      const amount = Math.abs(parseFloat(transaction.amount || 0))
      const dueDate = toDateInputValue(transaction.transaction_date)
      const label = formatDateOnly(transaction.transaction_date)
      const accountData = {
        amount,
        dueDate,
        competenceDate: dueDate,
        status: 'pending',
        categoryId: batchForm.subcategoryId || batchForm.categoryId,
        costCenterId: batchForm.costCenterId || null,
        financialAccountId: activeAccountId,
        observation: batchForm.description.trim()
      }

      if (batchForm.personType === 'supplier') accountData.supplierId = batchForm.personId
      else if (batchForm.personType === 'customer') accountData.customerId = batchForm.personId
      else if (batchForm.personType === 'user') accountData.userId = batchForm.personId

      try {
        const created = await accountPayableService.create(accountData)
        if (!created?.success || !created?.data?.id) {
          failures.push(`${label}: ${created?.message || 'não foi possível criar a conta'}`)
          continue
        }

        const linkResponse = await cashStatementService.linkTransaction(
          transaction.id,
          created.data.id,
          'account_payable'
        )
        if (!linkResponse?.success) {
          failures.push(`${label}: conta criada, mas a conciliação falhou`)
        }
      } catch (launchError) {
        failures.push(`${label}: ${launchError.response?.data?.message || 'erro ao lançar'}`)
      }
    }

    await Promise.all([loadTransactions(), loadBalance()])
    setBatchSubmitting(false)

    if (failures.length > 0) {
      setBatchError(failures.join(' · '))
      return
    }

    clearBatchSelection()
  }

  const batchParentCategories = batchCategories.filter((category) => !category.parent_id)
  const batchSubcategories = batchCategories.filter((category) => (
    category.parent_id && String(category.parent_id) === String(batchForm.categoryId)
  ))

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL' 
    }).format(val || 0)
  }

  const getAccountIcon = (account) => {
    // Mapear tipo de conta para ícone
    if (account.type === 'asset') return Landmark
    if (account.type === 'liability') return Building2
    return CreditCard
  }

  const hasActiveFilters = useMemo(() => {
    return typeFilter !== 'all' || statusFilter !== 'all' || amountMin !== '' || amountMax !== ''
  }, [typeFilter, statusFilter, amountMin, amountMax])

  const hasDateRangeFilter = Boolean(dateFrom && dateTo)

  const handlePeriodRangeChange = (start, end) => {
    let from = start
    let to = end
    if (from && to && from > to) {
      [from, to] = [to, from]
    }
    setDateFrom(from)
    setDateTo(to)
  }

  const handleClearPeriodFilter = () => {
    const range = getDefaultPeriodRange()
    setDateFrom(range.dateFrom)
    setDateTo(range.dateTo)
  }

  const resetFilters = () => {
    setTypeFilter('all')
    setStatusFilter('all')
    setAmountMin('')
    setAmountMax('')
    setSearchTerm('')
  }

  const handleExportExcel = () => {
    if (!filteredTransactions.length) {
      window.alert('Nenhum lançamento encontrado para exportar com os filtros atuais.')
      return
    }

    try {
      exportCashStatementToExcel({
        accountName: activeAccount?.name,
        dateFrom,
        dateTo,
        currentBalance: accountBalance?.currentBalance || 0,
        credits: summary.credits,
        debits: summary.debits,
        transactions: filteredTransactions
      })
    } catch (exportError) {
      console.error('Erro ao exportar extrato em Excel:', exportError)
      window.alert('Não foi possível gerar o Excel do extrato. Tente novamente.')
    }
  }

  const handleDeleteTransaction = async (transaction) => {
    if (!transaction) return
    
    // Confirmar exclusão
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir esta transação?\n\n` +
      `Descrição: ${transaction.description || 'Sem descrição'}\n` +
      `Valor: ${formatCurrency(Math.abs(parseFloat(transaction.amount || 0)))}\n` +
      `Data: ${formatDateOnly(transaction.transaction_date)}\n\n` +
      `Esta ação não pode ser desfeita.`
    )
    
    if (!confirmed) return
    
    try {
      setError(null)
      
      // Validar ID da transação
      const transactionId = parseInt(transaction.id)
      if (isNaN(transactionId) || transactionId <= 0) {
        setError('ID da transação inválido')
        return
      }
      
      console.log(`[Frontend] Tentando excluir transação ID: ${transactionId} (tipo: ${typeof transaction.id})`)
      
      const response = await cashStatementService.deleteTransaction(transactionId)
      
      if (response && response.success) {
        // Recarregar transações e saldo
        await Promise.all([
          loadTransactions(),
          loadBalance()
        ])
        setOpenDropdownId(null)
      } else {
        setError(response?.message || 'Erro ao excluir transação')
      }
    } catch (error) {
      console.error('Erro ao excluir transação:', error)
      setError(error.response?.data?.message || 'Erro ao excluir transação')
    }
  }

  if (!activeAccount && accounts.length === 0) {
    return (
      <div className="cash-statement-page-loading">
        <Loader2 className="cash-statement-loading-icon" size={48} />
        <p className="cash-statement-loading-text">Carregando contas financeiras...</p>
      </div>
    )
  }

  return (
    <div className="cash-statement-page">
      {/* Header com Seletor de Conta */}
      <div className="cash-statement-header">
        <div className="cash-statement-header-left">
          <div className="cash-statement-account-selector" ref={dropdownRef}>
            <button 
              onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
              className="cash-statement-account-button"
            >
              {activeAccount && (() => {
                const Icon = getAccountIcon(activeAccount)
                return (
                  <div className="cash-statement-account-icon-wrapper">
                    <Icon size={24} />
                  </div>
                )
              })()}
              <div className="cash-statement-account-info">
                <p className="cash-statement-account-label">
                  Conta Ativa
                </p>
                <h2 className="cash-statement-account-name">
                  {activeAccount?.name || 'Selecione uma conta'}
                </h2>
              </div>
              <ChevronDown 
                size={18} 
                className={`cash-statement-account-chevron ${isAccountDropdownOpen ? 'open' : ''}`} 
              />
            </button>

            {isAccountDropdownOpen && (
              <div className="cash-statement-account-dropdown">
                <div className="cash-statement-dropdown-header">
                  <p className="cash-statement-dropdown-title">
                    Selecionar Conta Bancária
                  </p>
                </div>
                <div className="cash-statement-dropdown-list cash-statement-scrollbar">
                  {accounts.map(acc => {
                    const Icon = getAccountIcon(acc)
                    const isDigital = acc.account_type === 'digital'
                    const isPhysical = acc.account_type === 'physical'
                    return (
                      <button 
                        key={acc.id}
                        onClick={() => {
                          const accountId = parseInt(acc.id, 10)
                          if (!isNaN(accountId) && accountId > 0) {
                            setActiveAccountId(accountId)
                          }
                          setIsAccountDropdownOpen(false)
                        }}
                        className={`cash-statement-dropdown-item ${activeAccountId === acc.id ? 'active' : ''}`}
                      >
                        <div className="cash-statement-dropdown-item-content">
                          <div className="cash-statement-dropdown-item-icon">
                            <Icon size={18} />
                          </div>
                          <div className="cash-statement-dropdown-item-text">
                            <p className="cash-statement-dropdown-item-name">
                              {acc.name}
                              {isDigital && (
                                <span style={{ 
                                  marginLeft: '0.5rem', 
                                  fontSize: '0.7rem', 
                                  color: '#3b82f6',
                                  fontWeight: 500
                                }}>
                                  (Digital)
                                </span>
                              )}
                              {isPhysical && (
                                <span style={{ 
                                  marginLeft: '0.5rem', 
                                  fontSize: '0.7rem', 
                                  color: '#6b7280',
                                  fontWeight: 500
                                }}>
                                  (Física)
                                </span>
                              )}
                            </p>
                            <p className="cash-statement-dropdown-item-code">
                              {acc.code || 'Sem código'}
                            </p>
                          </div>
                        </div>
                        {activeAccountId === acc.id && (
                          <CheckCircle2 size={16} style={{ color: 'var(--color-primary, #3b82f6)' }} />
                        )}
                      </button>
                    )
                  })}
                </div>
                <div className="cash-statement-dropdown-footer">
                  <button 
                    onClick={() => navigate('/erp/financeiro/contas')} 
                    className="cash-statement-dropdown-footer-button"
                  >
                    <Globe size={12} /> Gerenciar Contas
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="cash-statement-title-section">
            <h1 className="cash-statement-title">
              Extrato <span className="cash-statement-title-accent">de Caixa</span>
            </h1>
            <p className="cash-statement-subtitle">
              Sincronização Bancária OFX
            </p>
          </div>
        </div>

        <div className="cash-statement-header-actions">
          <button 
            onClick={handleOpenTransferModal}
            className="cash-statement-transfer-button"
            disabled={!activeAccount || accounts.length < 2}
            title={accounts.length < 2 ? 'Cadastre ao menos duas contas financeiras' : 'Registrar transferência entre contas'}
          >
            <ArrowRight size={16} /> Transferência
          </button>
          <button 
            onClick={() => {
              if (activeAccount?.account_type === 'physical') {
                alert('Apenas contas digitais podem importar extratos bancários (OFX).\n\nContas físicas são para dinheiro em espécie e não possuem extratos bancários.')
                return
              }
              setSelectedAccountForImport(activeAccountId)
              setIsModalOpen(true)
            }}
            className="cash-statement-import-button"
            disabled={!activeAccount || activeAccount.account_type === 'physical'}
            title={activeAccount?.account_type === 'physical' ? 'Apenas contas digitais podem importar OFX' : 'Importar arquivo OFX'}
          >
            <Upload size={16} /> Importar OFX
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="cash-statement-summary-grid">
        <div className="cash-statement-summary-card">
          <p className="cash-statement-summary-label">
            Saldo Atual em Conta
          </p>
          <p className="cash-statement-summary-value">
            {formatCurrency(accountBalance?.currentBalance || 0)}
          </p>
        </div>
        <div className="cash-statement-summary-card">
          <p className="cash-statement-summary-label credits">
            Total Entradas (Conta)
          </p>
          <p className="cash-statement-summary-value credits">
            {formatCurrency(summary.credits)}
          </p>
        </div>
        <div className="cash-statement-summary-card">
          <p className="cash-statement-summary-label debits">
            Total Saídas (Conta)
          </p>
          <p className="cash-statement-summary-value debits">
            {formatCurrency(summary.debits)}
          </p>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="cash-statement-table-container">
        <div className="cash-statement-table-header">
          <div className="cash-statement-search-wrapper">
            <Search className="cash-statement-search-icon" size={18} />
            <input 
              type="text" 
              placeholder={`Pesquisar em ${activeAccount?.name || 'conta'}...`} 
              className="cash-statement-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="cash-statement-table-actions">
            <button 
              onClick={() => setIsFilterOpen(true)}
              className={`cash-statement-filter-button ${hasActiveFilters ? 'active' : ''}`}
            >
              <Filter size={14} /> Filtros
              {hasActiveFilters && (
                <span className="cash-statement-filter-badge" />
              )}
            </button>
            <button
              ref={periodBtnRef}
              type="button"
              onClick={() => setIsPeriodOpen((prev) => !prev)}
              className={`cash-statement-filter-button ${hasDateRangeFilter ? 'active' : ''}`}
              title="Filtrar por período"
            >
              <Calendar size={14} /> Período
              {hasDateRangeFilter && (
                <span className="cash-statement-filter-badge" />
              )}
            </button>
            <CalendarRangePicker
              open={isPeriodOpen}
              onOpenChange={setIsPeriodOpen}
              anchorRef={periodBtnRef}
              startValue={dateFrom}
              endValue={dateTo}
              onRangeChange={handlePeriodRangeChange}
              onClear={handleClearPeriodFilter}
              title="Período"
              subtitle="Início e Fim"
            />
            <button
              type="button"
              className="cash-statement-export-button"
              onClick={handleExportExcel}
              disabled={loading || filteredTransactions.length === 0}
              title={filteredTransactions.length === 0 ? 'Nenhum lançamento para exportar' : 'Baixar extrato em Excel'}
            >
              <Download size={14} /> Excel
            </button>
          </div>
        </div>

        <div className="cash-statement-table-wrapper">
          {loading ? (
            <div className="cash-statement-loading">
              <Loader2 className="cash-statement-loading-icon" size={48} />
              <p className="cash-statement-loading-text">Carregando transações...</p>
            </div>
          ) : error ? (
            <div className="cash-statement-loading">
              <AlertCircle style={{ color: '#ef4444', marginBottom: '1rem' }} size={48} />
              <p className="cash-statement-loading-text">{error}</p>
            </div>
          ) : filteredTransactions.length > 0 ? (
            <table className="cash-statement-table">
              <thead>
                <tr>
                  <th>Data / Categoria</th>
                  <th>Lançamento Bancário</th>
                  <th>Valor</th>
                  <th style={{ textAlign: 'center' }}>Conciliação</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => {
                  // Usar o campo 'type' da transação, com fallback para o sinal do amount
                  const transactionType = transaction.type || (parseFloat(transaction.amount || 0) >= 0 ? 'credit' : 'debit')
                  const isCredit = transactionType === 'credit'
                  const isDebit = transactionType === 'debit'
                  const isTransfer = transaction.reference_type === 'cash_transfer'
                  const isReconciled = transaction.reconciled_type !== null || isTransfer
                  const rowKey = getStatementRowKey(transaction)
                  const canSelectForBatch = canBatchLaunchPayable(transaction, activeAccount)
                  const isBatchSelected = canSelectForBatch && batchSelectedKeys.includes(rowKey)
                  const canChangeTransferDestination = isTransfer &&
                    transaction.transfer_status === 'pending_destination' &&
                    parseInt(transaction.transfer_source_account_id, 10) === parseInt(activeAccountId, 10)
                  
                  return (
                    <tr
                      key={rowKey}
                      id={`cash-statement-row-${rowKey}`}
                      className={[
                        highlightedTransactionKey === rowKey ? 'is-highlighted' : '',
                        isBatchSelected ? 'is-batch-selected' : ''
                      ].filter(Boolean).join(' ')}
                    >
                      <td>
                        <div className="cash-statement-date-cell">
                          {canSelectForBatch && (
                            <button
                              type="button"
                              className={`cash-statement-batch-toggle ${isBatchSelected ? 'is-selected' : ''}`}
                              onClick={() => toggleBatchSelection(transaction)}
                              aria-pressed={isBatchSelected}
                              title={isBatchSelected ? 'Remover da seleção' : 'Selecionar para lançar em lote'}
                            >
                              {isBatchSelected ? <CircleCheck size={18} /> : <CircleDashed size={18} />}
                            </button>
                          )}
                          <div className="cash-statement-transaction-date">
                          <span className="cash-statement-transaction-date-value">
                            {formatDateOnly(transaction.transaction_date)}
                          </span>
                          <span className="cash-statement-transaction-category">
                            {transaction.category_name || 'Não Categorizado'}
                          </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="cash-statement-transaction-description">
                          <div className={`cash-statement-transaction-icon ${isCredit ? 'credit' : 'debit'}`}>
                            {isCredit ? <Plus size={14} /> : <Minus size={14} />}
                          </div>
                          <span className="cash-statement-transaction-text">
                            {transaction.description || 'Sem descrição'}
                            {isTransfer && transaction.counterparty_account_name && (
                              <small className="cash-statement-transfer-counterparty">
                                Conta contraparte: {transaction.counterparty_account_name}
                              </small>
                            )}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`cash-statement-transaction-amount ${isCredit ? 'credit' : 'debit'}`}>
                          {isDebit ? '-' : ''} {formatCurrency(Math.abs(parseFloat(transaction.amount || 0)))}
                        </span>
                      </td>
                      <td>
                        <div className="cash-statement-reconciliation-status">
                          {isReconciled ? (
                            <div className={`cash-statement-status-badge reconciled ${isTransfer ? 'transfer' : ''}`}>
                              <CheckCircle2 size={12} />
                              <span>{isTransfer ? 'Transferência' : 'Conciliado'}</span>
                              {transaction.reconciled_id && !isTransfer && (
                                <span className="cash-statement-reconciled-id">#{transaction.reconciled_id}</span>
                              )}
                              {transaction.transfer_id && (
                                <span className="cash-statement-reconciled-id">#{transaction.transfer_id}</span>
                              )}
                            </div>
                          ) : (
                            <button 
                              className="cash-statement-status-badge pending"
                              onClick={() => handleOpenLinkModal(transaction)}
                            >
                              <AlertCircle size={12} />
                              <span>Pendente</span>
                            </button>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="cash-statement-transaction-actions">
                          {!isReconciled && (
                            <button 
                              className="cash-statement-action-button" 
                              title="Vincular Operação ERP"
                              onClick={() => handleOpenLinkModal(transaction)}
                            >
                              <LinkIcon size={16} />
                            </button>
                          )}
                          <div 
                            className="cash-statement-action-dropdown-wrapper"
                            ref={(el) => {
                              if (el) actionDropdownRefs.current[transaction.id] = el
                            }}
                          >
                            <button 
                              className={`cash-statement-action-button more ${openDropdownId === transaction.id ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenDropdownId(openDropdownId === transaction.id ? null : transaction.id)
                              }}
                            >
                              <MoreVertical size={18} />
                            </button>
                            {openDropdownId === transaction.id && (
                              <div 
                                className="cash-statement-action-dropdown"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {isReconciled && !isTransfer && (
                                  <button 
                                    className="cash-statement-action-dropdown-item"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setOpenDropdownId(null)
                                      handleOpenLinkModal(transaction)
                                    }}
                                  >
                                    <LinkIcon size={14} />
                                    Ver Vínculo
                                  </button>
                                )}
                                {!isReconciled && (
                                  <button 
                                    className="cash-statement-action-dropdown-item"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setOpenDropdownId(null)
                                      handleOpenLinkModal(transaction)
                                    }}
                                  >
                                    <LinkIcon size={14} />
                                    Vincular Operação
                                  </button>
                                )}
                                {canChangeTransferDestination && (
                                  <>
                                    <button
                                      className="cash-statement-action-dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setOpenDropdownId(null)
                                        handleOpenEditTransferAmountModal(transaction)
                                      }}
                                    >
                                      <Pencil size={14} />
                                      Editar Valor
                                    </button>
                                    <button
                                      className="cash-statement-action-dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setOpenDropdownId(null)
                                        handleOpenChangeDestinationModal(transaction)
                                      }}
                                    >
                                      <ArrowRightLeft size={14} />
                                      Alterar Conta Destino
                                    </button>
                                  </>
                                )}
                                {!isTransfer && (
                                  <>
                                    <div className="cash-statement-action-dropdown-divider" />
                                    <button 
                                      className="cash-statement-action-dropdown-item danger"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setOpenDropdownId(null)
                                        handleDeleteTransaction(transaction)
                                      }}
                                    >
                                      <Trash2 size={14} />
                                      Excluir Lançamento
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <div className="cash-statement-empty">
              <FileText className="cash-statement-empty-icon" size={64} />
              <h4 className="cash-statement-empty-title">
                Nenhum lançamento encontrado
              </h4>
              <p className="cash-statement-empty-text">
                Ajuste os filtros ou importe um arquivo OFX
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal Importar OFX */}
      {isModalOpen && (
        <div className="cash-statement-modal-overlay">
          <div 
            className="cash-statement-modal-backdrop" 
            onClick={() => !isImporting && setIsModalOpen(false)} 
          />
          <div className="cash-statement-modal">
            <div className="cash-statement-modal-header">
              <div className="cash-statement-modal-header-content">
                <div className="cash-statement-modal-icon">
                  <Upload size={24} />
                </div>
                <div>
                  <h3 className="cash-statement-modal-title">
                    Importar OFX
                  </h3>
                  <p className="cash-statement-modal-subtitle">
                    Sincronização Direta Medik
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)} 
                disabled={isImporting} 
                className="cash-statement-modal-close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="cash-statement-modal-body">
              {isImporting ? (
                <div className="cash-statement-modal-processing">
                  <div className="cash-statement-modal-processing-icon">
                    <div className="cash-statement-modal-processing-ring" />
                    <Loader2 className="cash-statement-modal-processing-spinner" size={40} />
                  </div>
                  <div>
                    <h4 className="cash-statement-modal-processing-title">
                      Processando Arquivo
                    </h4>
                    <p className="cash-statement-modal-processing-text">
                      Extraindo lançamentos e atualizando saldos...
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="cash-statement-modal-section">
                    <label className="cash-statement-modal-label">
                      Confirmar Conta de Destino
                    </label>
                    <div className="cash-statement-account-list">
                      {accounts
                        .filter(acc => acc.account_type === 'digital')
                        .map(acc => {
                          const Icon = getAccountIcon(acc)
                          return (
                            <button 
                              key={acc.id}
                              onClick={() => {
                                const accountId = parseInt(acc.id, 10)
                                if (!isNaN(accountId) && accountId > 0) {
                                  setSelectedAccountForImport(accountId)
                                }
                              }}
                              className={`cash-statement-account-option ${selectedAccountForImport === acc.id ? 'selected' : ''}`}
                            >
                            <div className="cash-statement-account-option-content">
                              <div className="cash-statement-account-option-icon">
                                <Icon size={20} />
                              </div>
                              <div className="cash-statement-account-option-text">
                                <p className="cash-statement-account-option-name">
                                  {acc.name}
                                </p>
                                <p className="cash-statement-account-option-code">
                                  {acc.code || 'Sem código'}
                                </p>
                              </div>
                            </div>
                            <ChevronRight 
                              size={18} 
                              style={{ 
                                color: selectedAccountForImport === acc.id 
                                  ? 'var(--color-primary, #3b82f6)' 
                                  : '#e5e7eb' 
                              }} 
                            />
                          </button>
                          )
                        })}
                      {accounts.filter(acc => acc.account_type === 'digital').length === 0 && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                          <p>Nenhuma conta digital cadastrada.</p>
                          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                            Apenas contas digitais podem importar extratos bancários (OFX).
                          </p>
                          <button
                            onClick={() => {
                              setIsModalOpen(false)
                              navigate('/erp/financeiro/contas')
                            }}
                            style={{
                              marginTop: '1rem',
                              padding: '0.5rem 1rem',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            Criar Conta Digital
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="cash-statement-modal-section">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".ofx,.qfx,.xml"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <button 
                      onClick={handleFileSelect}
                      disabled={!selectedAccountForImport} 
                      className="cash-statement-modal-button"
                    >
                      Selecionar Arquivo .OFX <FileText size={16} />
                    </button>
                  </div>
                  {error && (
                    <div className="cash-statement-modal-error">
                      <p className="cash-statement-modal-error-text">{error}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Transferência entre Contas */}
      {isTransferModalOpen && (
        <div className="cash-statement-modal-overlay cash-statement-transfer-modal-overlay">
          <div 
            className="cash-statement-modal-backdrop" 
            onClick={() => {
              if (!isTransferSubmitting) {
                setIsTransferDestinationDropdownOpen(false)
                setIsTransferModalOpen(false)
              }
            }} 
          />
          <div className="cash-statement-modal cash-statement-transfer-modal">
            <div className="cash-statement-modal-header">
              <div className="cash-statement-modal-header-content">
                <div className="cash-statement-modal-icon">
                  <ArrowRight size={24} />
                </div>
                <div>
                  <h3 className="cash-statement-modal-title">
                    Transferência entre Contas
                  </h3>
                  <p className="cash-statement-modal-subtitle">
                    Origem: {activeAccount?.name || 'Conta ativa'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setIsTransferDestinationDropdownOpen(false)
                  setIsTransferModalOpen(false)
                }} 
                disabled={isTransferSubmitting} 
                className="cash-statement-modal-close"
              >
                <X size={24} />
              </button>
            </div>

            <div className="cash-statement-modal-body">
              <div className="cash-statement-modal-section">
                <label className="cash-statement-modal-label">Conta de Destino</label>
                <div
                  className="cash-statement-transfer-destination-selector"
                  ref={transferDestinationDropdownRef}
                >
                  <button
                    type="button"
                    className={`cash-statement-transfer-destination-trigger ${selectedDestinationAccount ? 'has-value' : ''}`}
                    onClick={() => !isTransferSubmitting && setIsTransferDestinationDropdownOpen(prev => !prev)}
                    disabled={isTransferSubmitting}
                    aria-expanded={isTransferDestinationDropdownOpen}
                    aria-haspopup="listbox"
                  >
                    {selectedDestinationAccount ? (
                      <span className="cash-statement-transfer-destination-trigger-content">
                        {(() => {
                          const Icon = getAccountIcon(selectedDestinationAccount)
                          return (
                            <span className="cash-statement-transfer-destination-trigger-icon">
                              <Icon size={18} />
                            </span>
                          )
                        })()}
                        <span className="cash-statement-transfer-destination-trigger-text">
                          <span className="cash-statement-transfer-destination-trigger-name">
                            {selectedDestinationAccount.name}
                          </span>
                          <span className="cash-statement-transfer-destination-trigger-meta">
                            {selectedDestinationAccount.account_type === 'digital' ? 'Digital' : 'Física'}
                            {selectedDestinationAccount.code ? ` · ${selectedDestinationAccount.code}` : ''}
                          </span>
                        </span>
                      </span>
                    ) : (
                      <span className="cash-statement-transfer-destination-placeholder">
                        Selecione a conta destino
                      </span>
                    )}
                    <ChevronDown
                      size={18}
                      className={`cash-statement-transfer-destination-chevron ${isTransferDestinationDropdownOpen ? 'open' : ''}`}
                    />
                  </button>

                  {isTransferDestinationDropdownOpen && (
                    <div className="cash-statement-transfer-destination-dropdown" role="listbox">
                      <div className="cash-statement-transfer-destination-dropdown-header">
                        <p className="cash-statement-dropdown-title">Conta de destino</p>
                      </div>
                      <div className="cash-statement-transfer-destination-dropdown-list cash-statement-scrollbar">
                        {transferDestinationAccounts.length > 0 ? transferDestinationAccounts.map(acc => {
                          const Icon = getAccountIcon(acc)
                          const isSelected = String(transferForm.destinationAccountId) === String(acc.id)
                          return (
                            <button
                              key={acc.id}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              className={`cash-statement-dropdown-item ${isSelected ? 'active' : ''}`}
                              onClick={() => {
                                handleTransferFormChange('destinationAccountId', String(acc.id))
                                setIsTransferDestinationDropdownOpen(false)
                              }}
                            >
                              <div className="cash-statement-dropdown-item-content">
                                <div className="cash-statement-dropdown-item-icon">
                                  <Icon size={18} />
                                </div>
                                <div className="cash-statement-dropdown-item-text">
                                  <p className="cash-statement-dropdown-item-name">
                                    {acc.name}
                                    <span className="cash-statement-transfer-account-type">
                                      {acc.account_type === 'digital' ? ' (Digital)' : ' (Física)'}
                                    </span>
                                  </p>
                                  <p className="cash-statement-dropdown-item-code">
                                    {acc.code || 'Sem código'}
                                  </p>
                                </div>
                              </div>
                              {isSelected && (
                                <CheckCircle2 size={16} style={{ color: 'var(--color-primary, #3b82f6)' }} />
                              )}
                            </button>
                          )
                        }) : (
                          <p className="cash-statement-transfer-empty">Nenhuma outra conta disponível.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="cash-statement-transfer-grid">
                <div className="cash-statement-modal-section">
                  <label className="cash-statement-modal-label">Valor</label>
                  <input
                    className="cash-statement-transfer-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={transferForm.amount}
                    onChange={(e) => handleTransferFormChange('amount', e.target.value)}
                    placeholder="0,00"
                    disabled={isTransferSubmitting || transferMode === 'digital-digital' || transferMode === 'digital-physical'}
                  />
                </div>

                <div className="cash-statement-modal-section cash-statement-transfer-date-field">
                  <label className="cash-statement-modal-label">Data</label>
                  <CalendarDatePicker
                    value={transferForm.transferDate}
                    onChange={(date) => handleTransferFormChange('transferDate', date)}
                    placeholder="Selecionar data"
                    title="Data da transferência"
                    disabled={isTransferSubmitting}
                  />
                </div>
              </div>

              <div className="cash-statement-modal-section">
                <label className="cash-statement-modal-label">Descrição</label>
                <input
                  className="cash-statement-transfer-input"
                  type="text"
                  value={transferForm.description}
                  onChange={(e) => handleTransferFormChange('description', e.target.value)}
                  placeholder="Ex: Transferência para conta reserva"
                  disabled={isTransferSubmitting}
                />
              </div>

              {transferMode === 'physical-digital' && (
                <div className="cash-statement-transfer-notice">
                  Será registrada apenas a saída da conta física. O crédito da conta digital deve ser conciliado quando o OFX for importado.
                </div>
              )}

              {transferMode === 'digital-physical' && (
                <div className="cash-statement-transfer-digital-section">
                  <div className="cash-statement-transfer-notice">
                    Selecione o débito já importado por OFX. A entrada será lançada na conta física e o extrato digital fica conciliado.
                  </div>
                  <label className="cash-statement-modal-label">Débito na Origem</label>
                  <div className="cash-statement-transfer-candidates">
                    {loadingSourceTransferTransactions ? (
                      <div className="cash-statement-link-loading">
                        <Loader2 className="cash-statement-link-loading-spinner" size={18} />
                        <span>Carregando débitos...</span>
                      </div>
                    ) : sourceDigitalDebitCandidates.length > 0 ? sourceDigitalDebitCandidates.map(transaction => (
                      <button
                        key={transaction.id}
                        type="button"
                        className={`cash-statement-transfer-candidate ${digitalTransferSelection.debitTransactionId === transaction.id ? 'selected' : ''}`}
                        onClick={() => {
                          const rawDate = String(transaction.transaction_date || '').slice(0, 10)
                          setDigitalTransferSelection(prev => ({
                            ...prev,
                            debitTransactionId: transaction.id,
                            creditTransactionId: ''
                          }))
                          setTransferForm(prev => ({
                            ...prev,
                            amount: Math.abs(parseFloat(transaction.amount || 0)).toFixed(2),
                            transferDate: /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : prev.transferDate
                          }))
                        }}
                        disabled={isTransferSubmitting}
                      >
                        <span>{transaction.description || 'Débito sem descrição'}</span>
                        <strong>{formatCurrency(Math.abs(parseFloat(transaction.amount || 0)))}</strong>
                        <small>{formatDateOnly(transaction.transaction_date)}</small>
                      </button>
                    )) : (
                      <p className="cash-statement-transfer-empty">Nenhum débito OFX pendente encontrado na origem.</p>
                    )}
                  </div>
                </div>
              )}

              {transferMode === 'digital-digital' && (
                <div className="cash-statement-transfer-digital-section">
                  <div className="cash-statement-transfer-notice">
                    Selecione dois lançamentos já importados por OFX. Nenhum extrato digital será criado ou editado manualmente.
                  </div>

                  <div className="cash-statement-transfer-columns">
                    <div>
                      <label className="cash-statement-modal-label">Débito na Origem</label>
                      <div className="cash-statement-transfer-candidates">
                        {loadingSourceTransferTransactions ? (
                          <div className="cash-statement-link-loading">
                            <Loader2 className="cash-statement-link-loading-spinner" size={18} />
                            <span>Carregando débitos...</span>
                          </div>
                        ) : sourceDigitalDebitCandidates.length > 0 ? sourceDigitalDebitCandidates.map(transaction => (
                          <button
                            key={transaction.id}
                            type="button"
                            className={`cash-statement-transfer-candidate ${digitalTransferSelection.debitTransactionId === transaction.id ? 'selected' : ''}`}
                            onClick={() => {
                              setDigitalTransferSelection(prev => ({
                                ...prev,
                                debitTransactionId: transaction.id,
                                creditTransactionId: ''
                              }))
                              handleTransferFormChange('amount', Math.abs(parseFloat(transaction.amount || 0)).toFixed(2))
                            }}
                            disabled={isTransferSubmitting}
                          >
                            <span>{transaction.description || 'Débito sem descrição'}</span>
                            <strong>{formatCurrency(Math.abs(parseFloat(transaction.amount || 0)))}</strong>
                            <small>{formatDateOnly(transaction.transaction_date)}</small>
                          </button>
                        )) : (
                          <p className="cash-statement-transfer-empty">Nenhum débito OFX pendente encontrado na origem.</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="cash-statement-modal-label">Crédito no Destino</label>
                      <div className="cash-statement-transfer-candidates">
                        {loadingDestinationTransactions ? (
                          <div className="cash-statement-link-loading">
                            <Loader2 className="cash-statement-link-loading-spinner" size={18} />
                            <span>Carregando créditos...</span>
                          </div>
                        ) : destinationDigitalCreditCandidates.length > 0 ? destinationDigitalCreditCandidates.map(transaction => (
                          <button
                            key={transaction.id}
                            type="button"
                            className={`cash-statement-transfer-candidate ${digitalTransferSelection.creditTransactionId === transaction.id ? 'selected' : ''}`}
                            onClick={() => setDigitalTransferSelection(prev => ({ ...prev, creditTransactionId: transaction.id }))}
                            disabled={isTransferSubmitting}
                          >
                            <span>{transaction.description || 'Crédito sem descrição'}</span>
                            <strong>{formatCurrency(Math.abs(parseFloat(transaction.amount || 0)))}</strong>
                            <small>{formatDateOnly(transaction.transaction_date)}</small>
                          </button>
                        )) : (
                          <p className="cash-statement-transfer-empty">Nenhum crédito OFX pendente encontrado no destino.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {transferError && (
                <div className="cash-statement-modal-error">
                  <p className="cash-statement-modal-error-text">{transferError}</p>
                </div>
              )}

              <button
                className="cash-statement-modal-button"
                onClick={handleSubmitTransfer}
                disabled={
                  isTransferSubmitting ||
                  !selectedDestinationAccount ||
                  (transferMode === 'digital-physical' && !digitalTransferSelection.debitTransactionId) ||
                  (transferMode !== 'digital-digital' && transferMode !== 'digital-physical' && !transferForm.amount) ||
                  (transferMode === 'digital-digital' && (!digitalTransferSelection.debitTransactionId || !digitalTransferSelection.creditTransactionId))
                }
              >
                {isTransferSubmitting ? (
                  <>
                    <Loader2 className="cash-statement-link-loading-spinner" size={16} />
                    Registrando...
                  </>
                ) : (
                  <>
                    Confirmar Transferência <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Alterar Conta Destino da Transferência */}
      {isChangeDestinationModalOpen && changeDestinationTransaction && (
        <div className="cash-statement-modal-overlay cash-statement-transfer-modal-overlay">
          <div
            className="cash-statement-modal-backdrop"
            onClick={() => {
              if (!isChangeDestinationSubmitting) {
                setIsChangeDestinationDropdownOpen(false)
                setIsChangeDestinationModalOpen(false)
              }
            }}
          />
          <div className="cash-statement-modal cash-statement-transfer-modal">
            <div className="cash-statement-modal-header">
              <div className="cash-statement-modal-header-content">
                <div className="cash-statement-modal-icon">
                  <ArrowRightLeft size={24} />
                </div>
                <div>
                  <h3 className="cash-statement-modal-title">
                    Alterar Conta Destino
                  </h3>
                  <p className="cash-statement-modal-subtitle">
                    Transferência #{changeDestinationTransaction.transfer_id} · {formatCurrency(Math.abs(parseFloat(changeDestinationTransaction.amount || 0)))}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsChangeDestinationDropdownOpen(false)
                  setIsChangeDestinationModalOpen(false)
                }}
                disabled={isChangeDestinationSubmitting}
                className="cash-statement-modal-close"
              >
                <X size={24} />
              </button>
            </div>

            <div className="cash-statement-modal-body">
              <div className="cash-statement-transfer-notice">
                {selectedChangeDestinationAccount?.account_type === 'physical' ? (
                  <>Ao escolher uma conta física, a transferência será concluída imediatamente com entrada no destino.</>
                ) : selectedChangeDestinationAccount?.account_type === 'digital' ? (
                  <>Esta transferência ainda aguarda conciliação do crédito no extrato digital. Escolha a conta correta para vincular o OFX depois.</>
                ) : (
                  <>Escolha a nova conta de destino. Contas digitais exigem conciliação via OFX; contas físicas concluem a transferência na hora.</>
                )}
              </div>

              <div className="cash-statement-modal-section">
                <label className="cash-statement-modal-label">Conta de Destino Atual</label>
                <p className="cash-statement-change-destination-current">
                  {changeDestinationTransaction.counterparty_account_name || 'Não informada'}
                </p>
              </div>

              <div className="cash-statement-modal-section">
                <label className="cash-statement-modal-label">Nova Conta de Destino</label>
                <div
                  className="cash-statement-transfer-destination-selector"
                  ref={changeDestinationDropdownRef}
                >
                  <button
                    type="button"
                    className={`cash-statement-transfer-destination-trigger ${selectedChangeDestinationAccount ? 'has-value' : ''}`}
                    onClick={() => !isChangeDestinationSubmitting && setIsChangeDestinationDropdownOpen(prev => !prev)}
                    disabled={isChangeDestinationSubmitting}
                    aria-expanded={isChangeDestinationDropdownOpen}
                    aria-haspopup="listbox"
                  >
                    {selectedChangeDestinationAccount ? (
                      <span className="cash-statement-transfer-destination-trigger-content">
                        {(() => {
                          const Icon = getAccountIcon(selectedChangeDestinationAccount)
                          return (
                            <span className="cash-statement-transfer-destination-trigger-icon">
                              <Icon size={18} />
                            </span>
                          )
                        })()}
                        <span className="cash-statement-transfer-destination-trigger-text">
                          <span className="cash-statement-transfer-destination-trigger-name">
                            {selectedChangeDestinationAccount.name}
                          </span>
                          <span className="cash-statement-transfer-destination-trigger-meta">
                            {selectedChangeDestinationAccount.account_type === 'digital' ? 'Digital' : 'Física'}
                            {selectedChangeDestinationAccount.code ? ` · ${selectedChangeDestinationAccount.code}` : ''}
                          </span>
                        </span>
                      </span>
                    ) : (
                      <span className="cash-statement-transfer-destination-placeholder">
                        Selecione a nova conta de destino
                      </span>
                    )}
                    <ChevronDown
                      size={18}
                      className={`cash-statement-transfer-destination-chevron ${isChangeDestinationDropdownOpen ? 'open' : ''}`}
                    />
                  </button>

                  {isChangeDestinationDropdownOpen && (
                    <div className="cash-statement-transfer-destination-dropdown" role="listbox">
                      <div className="cash-statement-transfer-destination-dropdown-header">
                        <p className="cash-statement-dropdown-title">Contas de destino disponíveis</p>
                      </div>
                      <div className="cash-statement-transfer-destination-dropdown-list cash-statement-scrollbar">
                        {changeDestinationAccounts.length > 0 ? changeDestinationAccounts.map(acc => {
                          const Icon = getAccountIcon(acc)
                          const isSelected = String(changeDestinationAccountId) === String(acc.id)
                          return (
                            <button
                              key={acc.id}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              className={`cash-statement-dropdown-item ${isSelected ? 'active' : ''}`}
                              onClick={() => {
                                setChangeDestinationAccountId(String(acc.id))
                                setIsChangeDestinationDropdownOpen(false)
                              }}
                            >
                              <div className="cash-statement-dropdown-item-content">
                                <div className="cash-statement-dropdown-item-icon">
                                  <Icon size={18} />
                                </div>
                                <div className="cash-statement-dropdown-item-text">
                                  <p className="cash-statement-dropdown-item-name">
                                    {acc.name}
                                    <span className="cash-statement-transfer-account-type">
                                      {acc.account_type === 'digital' ? ' (Digital)' : ' (Física)'}
                                    </span>
                                  </p>
                                  <p className="cash-statement-dropdown-item-code">
                                    {acc.code || 'Sem código'}
                                  </p>
                                </div>
                              </div>
                              {isSelected && (
                                <CheckCircle2 size={16} style={{ color: 'var(--color-primary, #3b82f6)' }} />
                              )}
                            </button>
                          )
                        }) : (
                          <p className="cash-statement-transfer-empty">Nenhuma outra conta disponível.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {changeDestinationError && (
                <div className="cash-statement-modal-error">
                  <p className="cash-statement-modal-error-text">{changeDestinationError}</p>
                </div>
              )}

              <button
                className="cash-statement-modal-button"
                onClick={handleSubmitChangeDestination}
                disabled={
                  isChangeDestinationSubmitting ||
                  !selectedChangeDestinationAccount ||
                  parseInt(changeDestinationAccountId, 10) === parseInt(changeDestinationTransaction.transfer_destination_account_id, 10)
                }
              >
                {isChangeDestinationSubmitting ? (
                  <>
                    <Loader2 className="cash-statement-link-loading-spinner" size={16} />
                    Salvando...
                  </>
                ) : (
                  <>
                    Confirmar Alteração <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Valor da Transferência */}
      {isEditTransferAmountModalOpen && editTransferAmountTransaction && (
        <div className="cash-statement-modal-overlay cash-statement-transfer-modal-overlay">
          <div
            className="cash-statement-modal-backdrop"
            onClick={() => {
              if (!isEditTransferAmountSubmitting) {
                setIsEditTransferAmountModalOpen(false)
              }
            }}
          />
          <div className="cash-statement-modal cash-statement-transfer-modal">
            <div className="cash-statement-modal-header">
              <div className="cash-statement-modal-header-content">
                <div className="cash-statement-modal-icon">
                  <Pencil size={24} />
                </div>
                <div>
                  <h3 className="cash-statement-modal-title">
                    Editar Valor da Transferência
                  </h3>
                  <p className="cash-statement-modal-subtitle">
                    Transferência #{editTransferAmountTransaction.transfer_id} · valor atual{' '}
                    {formatCurrency(Math.abs(parseFloat(editTransferAmountTransaction.amount || 0)))}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsEditTransferAmountModalOpen(false)}
                disabled={isEditTransferAmountSubmitting}
                className="cash-statement-modal-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="cash-statement-modal-body">
              <div className="cash-statement-modal-section">
                <label className="cash-statement-modal-label" htmlFor="edit-transfer-amount">
                  Novo valor de saída
                </label>
                <input
                  id="edit-transfer-amount"
                  className="cash-statement-transfer-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editTransferAmountValue}
                  onChange={(e) => setEditTransferAmountValue(e.target.value)}
                  placeholder="0,00"
                  disabled={isEditTransferAmountSubmitting}
                  autoFocus
                />
                <p className="cash-statement-filter-section-hint" style={{ marginTop: '0.5rem' }}>
                  Só é possível editar enquanto a transferência estiver pendente de conciliação no destino.
                </p>
              </div>

              {editTransferAmountError && (
                <div className="cash-statement-modal-error">
                  <p className="cash-statement-modal-error-text">{editTransferAmountError}</p>
                </div>
              )}

              <button
                className="cash-statement-modal-button"
                onClick={handleSubmitEditTransferAmount}
                disabled={isEditTransferAmountSubmitting || !editTransferAmountValue}
              >
                {isEditTransferAmountSubmitting ? (
                  <>
                    <Loader2 className="cash-statement-link-loading-spinner" size={16} />
                    Salvando...
                  </>
                ) : (
                  <>
                    Salvar Valor <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Side Panel: Filtros Avançados */}
      {isFilterOpen && (
        <>
          <div className="cash-statement-filter-overlay" onClick={() => setIsFilterOpen(false)} />
          <aside className="cash-statement-filter-sidebar cash-statement-scrollbar">
            <div className="cash-statement-filter-header">
              <div className="cash-statement-filter-header-content">
                <div className="cash-statement-filter-header-icon">
                  <Filter size={24} />
                </div>
                <div>
                  <h3 className="cash-statement-filter-header-title">
                    Filtros
                  </h3>
                  <p className="cash-statement-filter-header-subtitle">
                    Refinar resultados
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsFilterOpen(false)} 
                className="cash-statement-filter-close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="cash-statement-filter-content">
              {/* Tipo de Lançamento */}
              <div className="cash-statement-filter-section">
                <h4 className="cash-statement-filter-section-title">
                  Tipo de Lançamento
                </h4>
                <div className="cash-statement-filter-options">
                  {[
                    { id: 'all', label: 'Todos os Lançamentos', icon: Layers },
                    { id: 'credit', label: 'Créditos (Entradas)', icon: ArrowUpCircle },
                    { id: 'debit', label: 'Débitos (Saídas)', icon: ArrowDownCircle },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setTypeFilter(opt.id)}
                      className={`cash-statement-filter-option ${typeFilter === opt.id ? 'selected' : ''}`}
                    >
                      <div className="cash-statement-filter-option-content">
                        <opt.icon 
                          size={18} 
                          className="cash-statement-filter-option-icon"
                        />
                        <span className="cash-statement-filter-option-label">
                          {opt.label}
                        </span>
                      </div>
                      {typeFilter === opt.id && (
                        <Check size={16} className="cash-statement-filter-option-check" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status de Conciliação */}
              <div className="cash-statement-filter-section">
                <h4 className="cash-statement-filter-section-title">
                  Conciliação ERP
                </h4>
                <div className="cash-statement-filter-options">
                  {[
                    { id: 'all', label: 'Ambos os Status', icon: Sparkles },
                    { id: 'reconciled', label: 'Já Conciliados', icon: CheckCircle2 },
                    { id: 'pending', label: 'Pendentes de Vínculo', icon: AlertCircle },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setStatusFilter(opt.id)}
                      className={`cash-statement-filter-option ${statusFilter === opt.id ? 'selected' : ''}`}
                    >
                      <div className="cash-statement-filter-option-content">
                        <opt.icon 
                          size={18} 
                          className="cash-statement-filter-option-icon"
                        />
                        <span className="cash-statement-filter-option-label">
                          {opt.label}
                        </span>
                      </div>
                      {statusFilter === opt.id && (
                        <Check size={16} className="cash-statement-filter-option-check" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Intervalo de valor */}
              <div className="cash-statement-filter-section">
                <h4 className="cash-statement-filter-section-title">
                  Valor do Extrato
                </h4>
                <p className="cash-statement-filter-section-hint">
                  Busque lançamentos pelo valor absoluto (mínimo e/ou máximo).
                </p>
                <div className="cash-statement-filter-amount-grid">
                  <div className="cash-statement-filter-amount-field">
                    <label className="cash-statement-modal-label" htmlFor="cash-statement-amount-min">
                      Mínimo
                    </label>
                    <input
                      id="cash-statement-amount-min"
                      className="cash-statement-transfer-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={amountMin}
                      onChange={(e) => setAmountMin(e.target.value)}
                      placeholder="0,00"
                    />
                  </div>
                  <div className="cash-statement-filter-amount-field">
                    <label className="cash-statement-modal-label" htmlFor="cash-statement-amount-max">
                      Máximo
                    </label>
                    <input
                      id="cash-statement-amount-max"
                      className="cash-statement-transfer-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={amountMax}
                      onChange={(e) => setAmountMax(e.target.value)}
                      placeholder="0,00"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="cash-statement-filter-footer">
              <button 
                onClick={resetFilters}
                className="cash-statement-filter-reset"
              >
                <Trash2 size={14} /> Limpar Filtros
              </button>
              <button 
                onClick={() => setIsFilterOpen(false)}
                className="cash-statement-filter-apply"
              >
                Aplicar Filtros
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Modal Vincular Operação ERP */}
      {isLinkModalOpen && activeTransaction && (
        <div className="cash-statement-link-modal-overlay">
          <div 
            className="cash-statement-link-modal-backdrop" 
            onClick={() => setIsLinkModalOpen(false)} 
          />
          <div className="cash-statement-link-modal">
            <div className="cash-statement-link-modal-header">
              <div className="cash-statement-link-modal-header-top">
                <div className="cash-statement-link-modal-header-content">
                  <div className="cash-statement-link-modal-icon">
                    <LinkIcon size={22} />
                  </div>
                  <h3 className="cash-statement-link-modal-title">
                    Conciliar Lançamento
                  </h3>
                </div>
                <button 
                  onClick={() => setIsLinkModalOpen(false)} 
                  className="cash-statement-link-modal-close"
                  type="button"
                  aria-label="Fechar"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="cash-statement-link-search-input-wrapper cash-statement-link-search-input-wrapper--header">
                <Search className="cash-statement-link-search-icon" size={16} />
                <input 
                  type="search" 
                  className="cash-statement-link-search-input"
                  placeholder="Buscar por ID, cliente, fornecedor ou fit_id…"
                  value={linkSearchTerm}
                  onChange={(e) => setLinkSearchTerm(e.target.value)}
                  autoComplete="off"
                />
                {linkSearchTerm && (
                  <button
                    type="button"
                    className="cash-statement-link-search-clear"
                    onClick={() => setLinkSearchTerm('')}
                    aria-label="Limpar busca"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {linkSuccessMessage && (
              <div className="cash-statement-link-success-banner" role="status">
                <CheckCircle2 size={16} />
                <span>{linkSuccessMessage}</span>
              </div>
            )}

            {/* Informação do Lançamento de Origem */}
            <div className="cash-statement-link-transaction-info">
              <div className="cash-statement-link-transaction-content">
                <div className={`cash-statement-link-transaction-type-badge ${(() => {
                  const transactionType = activeTransaction.type || (parseFloat(activeTransaction.amount || 0) >= 0 ? 'credit' : 'debit')
                  return transactionType === 'debit' ? 'debit' : ''
                })()}`}>
                  {(() => {
                    const transactionType = activeTransaction.type || (parseFloat(activeTransaction.amount || 0) >= 0 ? 'credit' : 'debit')
                    return transactionType === 'credit' ? 'C' : 'D'
                  })()}
                </div>
                <div>
                  <p className="cash-statement-link-transaction-label">
                    Origem Extrato
                  </p>
                  <p className="cash-statement-link-transaction-description">
                    {activeTransaction.description || 'Sem descrição'}
                  </p>
                </div>
              </div>
              <div className="cash-statement-link-transaction-value">
                <p className="cash-statement-link-transaction-amount">
                  {formatCurrency(Math.abs(parseFloat(activeTransaction.amount || 0)))}
                </p>
                <p className="cash-statement-link-transaction-date">
                  {formatDateOnly(activeTransaction.transaction_date)}
                </p>
              </div>
            </div>

            <div className="cash-statement-link-modal-body">
              {/* Sugestões de Combinações */}
              {erpSuggestions.length > 0 && (
                <div className="cash-statement-link-suggestions">
                  <div className="cash-statement-link-section-header">
                    <Sparkles className="cash-statement-link-section-icon" size={16} />
                    <h4 className="cash-statement-link-section-title">
                      Sugestões de Valor Idêntico (Smart Match)
                    </h4>
                  </div>
                  <div className="cash-statement-link-suggestions-list">
                    {erpSuggestions.map(title => {
                      // Verificar se esta conciliação já está vinculada a esta transação
                      const isAlreadyLinked = title.erpType === 'card_reconciliation' && 
                        linkedReconciliationIds.includes(String(title.id))
                      
                      // Verificar se está selecionada
                      const isSelected = selectedTitlesToLink.some(t => t.id === title.id)
                      
                      return (
                      <button 
                        key={title.id}
                        onClick={() => handleToggleTitleSelection(title)}
                        className={`cash-statement-link-suggestion-item smart-match ${isSelected ? 'selected' : ''} ${isAlreadyLinked ? 'disabled' : ''}`}
                        disabled={isAlreadyLinked}
                        title={isAlreadyLinked ? 'Esta conciliação já está vinculada' : isSelected ? 'Clique para desmarcar' : 'Clique para selecionar'}
                      >
                        <div className="cash-statement-link-suggestion-content">
                          <div className="cash-statement-link-suggestion-icon smart-match">
                            <Hash size={18} />
                          </div>
                          <div className="cash-statement-link-suggestion-text">
                            <p className="cash-statement-link-suggestion-entity">
                              {title.entity}
                              {title.erpType === 'card_reconciliation' && (
                                <span style={{ 
                                  marginLeft: '0.5rem', 
                                  fontSize: '0.7rem', 
                                  color: '#3b82f6',
                                  fontWeight: 500
                                }}>
                                  (Cartão)
                                </span>
                              )}
                            </p>
                            <p className="cash-statement-link-suggestion-details">
                              {title.erpType === 'card_reconciliation' 
                                ? `Conciliação #${title.id} • ${formatCurrency(title.value)}`
                                : `#${title.id} • ${title.description || formatTitleValueLabel(title, formatCurrency)}`
                              }
                            </p>
                          </div>
                        </div>
                        {isSelected ? (
                          <Check size={20} className="cash-statement-link-suggestion-check" style={{ color: '#10b981' }} />
                        ) : (
                          <ArrowRight size={20} className="cash-statement-link-suggestion-arrow" />
                        )}
                      </button>
                      )
                    })}
                  </div>
                  {selectedTitlesToLink.length > 0 && (
                    <div style={{ marginTop: '1rem', padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                      <p style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                        {selectedTitlesToLink.length === 1 
                          ? `Conciliação selecionada: #${selectedTitlesToLink[0].id}`
                          : `${selectedTitlesToLink.length} conciliações selecionadas`
                        }
                      </p>
                      <div style={{ marginBottom: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        {selectedTitlesToLink.map((title, idx) => (
                          <div key={title.id} style={{ marginBottom: '0.25rem' }}>
                            #{title.id}: {formatCurrency(title.value)}
                          </div>
                        ))}
                      </div>
                      <p style={{ marginBottom: '1rem', fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' }}>
                        Total: {formatCurrency(selectedTitlesToLink.reduce((sum, t) => sum + (t.value || 0), 0))}
                      </p>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleConfirmLink()}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}
                        >
                          Confirmar Vínculo{selectedTitlesToLink.length > 1 ? 's' : ''}
                        </button>
                        <button
                          onClick={() => setSelectedTitlesToLink([])}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'transparent',
                            color: '#6b7280',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Limpar Seleção
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}


              {erpCombinations.length > 0 && (
                <div className="cash-statement-link-suggestions" style={{ marginBottom: '1.5rem' }}>
                  <div className="cash-statement-link-section-header">
                    <Layers className="cash-statement-link-section-icon" size={16} />
                    <h4 className="cash-statement-link-section-title">
                      Combinações Sugeridas (Múltiplas Contas)
                    </h4>
                  </div>
                  <p className="cash-statement-link-approximate-hint">
                    Apenas títulos do mesmo fornecedor, cliente ou usuário cuja soma iguala o extrato.
                  </p>
                  <div className="cash-statement-link-suggestions-list">
                    {erpCombinations.map((combination, comboIdx) => {
                      const comboSum = combination.reduce((sum, t) => sum + calculateErpTitleRemainingBalance(t), 0)
                      const transactionValue = Math.abs(parseFloat(activeTransaction.amount || 0))
                      const isExactMatch = Math.abs(comboSum - transactionValue) < 0.01
                      const allSelected = combination.every(t => selectedTitlesToLink.some(st => st.id === t.id))
                      const partyLabel = combination[0]?.entity || 'Mesma parte'
                      
                      return (
                        <button
                          key={`combo-${comboIdx}`}
                          onClick={() => {
                            if (allSelected) {
                              // Se todas já estão selecionadas, desmarcar todas
                              setSelectedTitlesToLink(prev => 
                                prev.filter(t => !combination.some(ct => ct.id === t.id))
                              )
                            } else {
                              // Adicionar todas as contas da combinação
                              setSelectedTitlesToLink(prev => {
                                const newSelection = [...prev]
                                combination.forEach(title => {
                                  if (!newSelection.some(t => t.id === title.id)) {
                                    newSelection.push(title)
                                  }
                                })
                                return newSelection
                              })
                            }
                          }}
                          className={`cash-statement-link-suggestion-item ${allSelected ? 'selected' : ''}`}
                          style={{
                            border: isExactMatch ? '2px solid #10b981' : '1px solid #e5e7eb',
                            background: allSelected ? '#f0fdf4' : 'white'
                          }}
                          title={allSelected ? 'Clique para desmarcar todas' : 'Clique para selecionar todas as contas desta combinação'}
                        >
                          <div className="cash-statement-link-suggestion-content" style={{ width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <Layers size={16} style={{ color: isExactMatch ? '#10b981' : '#6b7280' }} />
                              <span style={{ 
                                fontSize: '0.875rem', 
                                fontWeight: 600,
                                color: isExactMatch ? '#10b981' : '#1f2937'
                              }}>
                                {combination.length} conta{combination.length > 1 ? 's' : ''} · {partyLabel} · Total: {formatCurrency(comboSum)}
                                {isExactMatch && (
                                  <span style={{ 
                                    marginLeft: '0.5rem', 
                                    fontSize: '0.75rem', 
                                    color: '#10b981',
                                    fontWeight: 500
                                  }}>
                                    ✓ Valor exato
                                  </span>
                                )}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              {combination.map((title, idx) => (
                                <div key={title.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>#{title.id} - {title.description || title.entity || 'Sem descrição'}</span>
                                  <span style={{ fontWeight: 500 }}>{formatCurrency(calculateErpTitleRemainingBalance(title))}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          {allSelected ? (
                            <Check size={20} className="cash-statement-link-suggestion-check" style={{ color: '#10b981' }} />
                          ) : (
                            <ArrowRight size={20} className="cash-statement-link-suggestion-arrow" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                  {/* Confirmação de vínculo para combinações */}
                  {erpCombinations.some(combo => 
                    combo.every(t => selectedTitlesToLink.some(st => st.id === t.id))
                  ) && (() => {
                    const selectedCombination = erpCombinations.find(combo => 
                      combo.every(t => selectedTitlesToLink.some(st => st.id === t.id))
                    )
                    if (!selectedCombination) return null
                    
                    const comboSum = selectedCombination.reduce((sum, t) => sum + calculateErpTitleRemainingBalance(t), 0)
                    const transactionValue = Math.abs(parseFloat(activeTransaction.amount || 0))
                    
                    return (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981' }}>
                        <p style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#10b981' }}>
                          Combinação selecionada: {selectedCombination.length} conta{selectedCombination.length > 1 ? 's' : ''}
                        </p>
                        <div style={{ marginBottom: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                          {selectedCombination.map((title, idx) => (
                            <div key={title.id} style={{ marginBottom: '0.25rem', display: 'flex', justifyContent: 'space-between' }}>
                              <span>#{title.id} - {title.description || title.entity || 'Sem descrição'}</span>
                              <span style={{ fontWeight: 500 }}>{formatCurrency(Math.abs(title.value))}</span>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' }}>
                              Total da combinação:
                            </span>
                            <span style={{ fontSize: '1rem', fontWeight: 700, color: '#10b981' }}>
                              {formatCurrency(comboSum)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              Valor da transação:
                            </span>
                            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#1f2937' }}>
                              {formatCurrency(transactionValue)}
                            </span>
                          </div>
                          {Math.abs(comboSum - transactionValue) < 0.01 && (
                            <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#d1fae5', borderRadius: '4px', textAlign: 'center' }}>
                              <span style={{ fontSize: '0.75rem', color: '#065f46', fontWeight: 500 }}>
                                ✓ Valores correspondem perfeitamente
                              </span>
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleConfirmLink(selectedCombination)}
                            style={{
                              flex: 1,
                              padding: '0.75rem 1rem',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.background = '#059669'}
                            onMouseLeave={(e) => e.target.style.background = '#10b981'}
                          >
                            Confirmar Vínculo{selectedCombination.length > 1 ? 's' : ''}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedTitlesToLink(prev => 
                                prev.filter(t => !selectedCombination.some(ct => ct.id === t.id))
                              )
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              background: 'transparent',
                              color: '#6b7280',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.875rem'
                            }}
                          >
                            Limpar
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Títulos com valor próximo ao extrato */}
              {erpApproximateMatches.length > 0 && (
                <div className="cash-statement-link-suggestions" style={{ marginBottom: '1.5rem' }}>
                  <div className="cash-statement-link-section-header">
                    <AlertCircle className="cash-statement-link-section-icon" size={16} />
                    <h4 className="cash-statement-link-section-title">
                      Títulos com Valor Próximo
                    </h4>
                  </div>
                  <p className="cash-statement-link-approximate-hint">
                    Saldo pendente diferente do extrato — você poderá escolher pagamento parcial ou quitação com abatimento.
                  </p>
                  <div className="cash-statement-link-suggestions-list">
                    {erpApproximateMatches.map(title => {
                      const isSelected = selectedTitlesToLink.some(t => t.id === title.id)
                      const remainingBalance = calculateErpTitleRemainingBalance(title)
                      const transactionValue = Math.abs(parseFloat(activeTransaction.amount || 0))
                      const difference = remainingBalance - transactionValue

                      return (
                        <button
                          key={title.id}
                          onClick={() => handleToggleTitleSelection(title)}
                          className={`cash-statement-link-suggestion-item approximate ${isSelected ? 'selected' : ''}`}
                          title={isSelected ? 'Clique para desmarcar' : 'Clique para selecionar'}
                        >
                          <div className="cash-statement-link-suggestion-content">
                            <div className="cash-statement-link-suggestion-icon approximate">
                              <FileText size={18} />
                            </div>
                            <div className="cash-statement-link-suggestion-text">
                              <p className="cash-statement-link-suggestion-entity">{title.entity}</p>
                              <p className="cash-statement-link-suggestion-details">
                                #{title.id} • {title.description || 'Sem descrição'}
                              </p>
                              <p className="cash-statement-link-approximate-values">
                                Saldo: {formatCurrency(remainingBalance)} • Diferença: {formatCurrency(difference)}
                              </p>
                            </div>
                          </div>
                          {isSelected ? (
                            <Check size={20} className="cash-statement-link-suggestion-check" style={{ color: '#10b981' }} />
                          ) : (
                            <ArrowRight size={20} className="cash-statement-link-suggestion-arrow" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                  {selectedTitlesToLink.length > 0 && erpApproximateMatches.some(t => selectedTitlesToLink.some(st => st.id === t.id)) && (
                    <div className="cash-statement-link-selection-bar">
                      <p className="cash-statement-link-selection-count">
                        {selectedTitlesToLink.length === 1
                          ? `Selecionado: #${selectedTitlesToLink[0].id}`
                          : `${selectedTitlesToLink.length} títulos selecionados`}
                      </p>
                      <div className="cash-statement-link-selection-actions">
                        <button type="button" onClick={() => handleConfirmLink()} className="cash-statement-link-confirm-btn">
                          Confirmar
                        </button>
                        <button type="button" onClick={() => setSelectedTitlesToLink([])} className="cash-statement-link-clear-btn">
                          Limpar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Resultados da busca (campo fica no cabeçalho) */}
              <div className="cash-statement-link-search">
                {linkSearchTerm ? (
                  <div className="cash-statement-link-section-header">
                    <Search className="cash-statement-link-section-icon" size={16} />
                    <h4 className="cash-statement-link-section-title">
                      Resultados da busca
                    </h4>
                  </div>
                ) : null}

                {loadingTitles ? (
                  <div className="cash-statement-link-loading">
                    <Loader2 className="cash-statement-link-loading-spinner" size={20} />
                    <span>Buscando títulos...</span>
                  </div>
                ) : linkSearchTerm && erpSearchResults.length > 0 ? (
                  <>
                    <div className="cash-statement-link-results">
                      {erpSearchResults.map(title => {
                        const isAlreadyLinked = title.erpType === 'card_reconciliation' && 
                          linkedReconciliationIds.includes(String(title.id))
                        const isSelected = selectedTitlesToLink.some(t => t.id === title.id)
                        
                        return (
                        <button 
                          key={title.id}
                          onClick={() => handleToggleTitleSelection(title)}
                          className={`cash-statement-link-result-item ${isSelected ? 'selected' : ''} ${isAlreadyLinked ? 'disabled' : ''}`}
                          disabled={isAlreadyLinked}
                          title={isAlreadyLinked ? 'Esta conciliação já está vinculada' : isSelected ? 'Clique para desmarcar' : 'Clique para selecionar'}
                        >
                          <div className="cash-statement-link-result-content">
                            <div className="cash-statement-link-result-icon">
                              <FileText size={14} />
                            </div>
                            <div className="cash-statement-link-result-text">
                              <p className="cash-statement-link-result-entity">
                                {title.entity}
                                {title.erpType === 'card_reconciliation' && (
                                  <span style={{ 
                                    marginLeft: '0.5rem', 
                                    fontSize: '0.7rem', 
                                    color: '#3b82f6',
                                    fontWeight: 500
                                  }}>
                                    (Cartão)
                                  </span>
                                )}
                              </p>
                              <p className="cash-statement-link-result-details">
                                {title.erpType === 'card_reconciliation' 
                                  ? `Conciliação #${title.id} • ${formatCurrency(title.value)}`
                                  : `#${title.id} • ${formatTitleValueLabel(title, formatCurrency)}`
                                }
                              </p>
                            </div>
                          </div>
                        {isSelected ? (
                          <Check size={16} className="cash-statement-link-result-check" style={{ color: '#10b981' }} />
                        ) : (
                          <Plus size={16} className="cash-statement-link-result-plus" />
                        )}
                      </button>
                      )
                    })}
                  </div>
                  {selectedTitlesToLink.length > 0 && erpSearchResults.some(t => selectedTitlesToLink.some(st => st.id === t.id)) && (
                    <div style={{ marginTop: '1rem', padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                      <p style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>
                        {selectedTitlesToLink.length === 1 
                          ? `Conciliação selecionada: #${selectedTitlesToLink[0].id}`
                          : `${selectedTitlesToLink.length} conciliações selecionadas`
                        }
                      </p>
                      <div style={{ marginBottom: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        {selectedTitlesToLink.map((title, idx) => (
                          <div key={title.id} style={{ marginBottom: '0.25rem' }}>
                            #{title.id}: {formatCurrency(title.value)}
                          </div>
                        ))}
                      </div>
                      <p style={{ marginBottom: '1rem', fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' }}>
                        Total: {formatCurrency(selectedTitlesToLink.reduce((sum, t) => sum + (t.value || 0), 0))}
                      </p>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleConfirmLink()}
                          style={{
                            flex: 1,
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: 500
                          }}
                        >
                          Confirmar Vínculo{selectedTitlesToLink.length > 1 ? 's' : ''}
                        </button>
                        <button
                          onClick={() => setSelectedTitlesToLink([])}
                          style={{
                            padding: '0.5rem 1rem',
                            background: 'transparent',
                            color: '#6b7280',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          Limpar Seleção
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                ) : linkSearchTerm && !loadingTitles ? (
                  <div className="cash-statement-link-empty">
                    <p className="cash-statement-link-empty-text">
                      Nenhum título encontrado com "{linkSearchTerm}"
                    </p>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="cash-statement-link-modal-footer">
              {getTransactionType(activeTransaction) === 'debit' && !activeTransaction.reconciled_type ? (
                <a
                  href={`${window.location.origin}/erp/financeiro/contas-pagar/nova`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleNavigateToNewPayable}
                  className="cash-statement-link-create-payable-button"
                  title="Lançar conta a pagar com dados do extrato"
                >
                  <Plus size={16} />
                  <span>Lançar conta a pagar</span>
                </a>
              ) : (
                <span className="cash-statement-link-modal-footer-spacer" />
              )}
              <button 
                type="button"
                onClick={() => setIsLinkModalOpen(false)} 
                className="cash-statement-link-modal-cancel"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmação pagamento parcial / quitação final */}
      {partialPaymentConfirm && (
        <div className="cash-statement-partial-modal-overlay">
          <div
            className="cash-statement-partial-modal-backdrop"
            onClick={() => setPartialPaymentConfirm(null)}
          />
          <div className="cash-statement-partial-modal" role="dialog" aria-modal="true">
            <div className="cash-statement-partial-modal-header">
              <AlertCircle size={22} className="cash-statement-partial-modal-icon" />
              <h3>Divergência de valor detectada</h3>
            </div>
            <p className="cash-statement-partial-modal-text">
              O valor no extrato é diferente do saldo pendente do título. Escolha como deseja finalizar a conciliação.
            </p>
            <div className="cash-statement-partial-modal-details">
              <div>
                <span>Valor no extrato</span>
                <strong>{formatCurrency(partialPaymentConfirm.transactionValue)}</strong>
              </div>
              <div>
                <span>Saldo do título</span>
                <strong>{formatCurrency(partialPaymentConfirm.remainingBefore)}</strong>
              </div>
              <div>
                <span>Saldo após conciliar (parcial)</span>
                <strong>{formatCurrency(partialPaymentConfirm.remainingAfter)}</strong>
              </div>
              <div className="cash-statement-partial-modal-adjustment">
                <span>Abatimento na quitação final</span>
                <strong>{formatCurrency(partialPaymentConfirm.adjustmentAmount)}</strong>
              </div>
            </div>
            <p className="cash-statement-partial-modal-hint">
              Título #{partialPaymentConfirm.title.id} — {partialPaymentConfirm.title.description}
            </p>
            <div className="cash-statement-partial-modal-options">
              <div className="cash-statement-partial-modal-option">
                <h4>Pagamento parcial</h4>
                <p>Registra {formatCurrency(partialPaymentConfirm.transactionValue)} e mantém {formatCurrency(partialPaymentConfirm.remainingAfter)} pendente.</p>
                <button
                  type="button"
                  className="cash-statement-partial-modal-confirm partial"
                  onClick={() => handleConfirmLink(partialPaymentConfirm.titles, SETTLEMENT_MODES.PARTIAL)}
                >
                  Confirmar pagamento parcial
                </button>
              </div>
              <div className="cash-statement-partial-modal-option">
                <h4>Quitação final com abatimento</h4>
                <p>Encerra o título aplicando abatimento de {formatCurrency(partialPaymentConfirm.adjustmentAmount)} (desconto/ajuste).</p>
                <button
                  type="button"
                  className="cash-statement-partial-modal-confirm full"
                  onClick={() => handleConfirmLink(partialPaymentConfirm.titles, SETTLEMENT_MODES.FULL_WITH_ADJUSTMENT)}
                >
                  Quitar como pagamento final
                </button>
              </div>
            </div>
            <div className="cash-statement-partial-modal-actions">
              <button
                type="button"
                className="cash-statement-partial-modal-cancel"
                onClick={() => setPartialPaymentConfirm(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal taxa de boleto — face cheia + despesa */}
      {boletoFeeConfirm && (
        <div className="cash-statement-partial-modal-overlay">
          <div
            className="cash-statement-partial-modal-backdrop"
            onClick={() => setBoletoFeeConfirm(null)}
          />
          <div className="cash-statement-partial-modal cash-statement-boleto-fee-modal" role="dialog" aria-modal="true">
            <div className="cash-statement-partial-modal-header">
              <FileText size={22} className="cash-statement-partial-modal-icon" />
              <h3>Taxa de boleto</h3>
            </div>
            <p className="cash-statement-partial-modal-text">
              Informe a taxa de cada boleto. O título será quitado no valor de face e a taxa será lançada como despesa
              (categoria Taxa de Boleto PagBank). A soma dos líquidos deve fechar com o extrato.
            </p>
            {boletoFeeConfirm.localError && (
              <p className="cash-statement-boleto-fee-error">{boletoFeeConfirm.localError}</p>
            )}
            <div className="cash-statement-partial-modal-details">
              <div>
                <span>Valor no extrato</span>
                <strong>{formatCurrency(boletoFeeConfirm.transactionValue)}</strong>
              </div>
              <div>
                <span>Soma dos líquidos</span>
                <strong>
                  {formatCurrency(
                    boletoFeeConfirm.titles.reduce((sum, t) => {
                      const remaining = calculateErpTitleRemainingBalance(t)
                      const fee = parseFloat(String(boletoFeeConfirm.fees[t.id] ?? '').replace(',', '.')) || 0
                      return sum + Math.max(0, remaining - fee)
                    }, 0)
                  )}
                </strong>
              </div>
            </div>

            <div className="cash-statement-boleto-fee-list">
              {boletoFeeConfirm.titles.map((title) => {
                const remaining = calculateErpTitleRemainingBalance(title)
                const fee = parseFloat(String(boletoFeeConfirm.fees[title.id] ?? '').replace(',', '.')) || 0
                const net = Math.max(0, remaining - fee)
                return (
                  <div key={title.id} className="cash-statement-boleto-fee-row">
                    <div className="cash-statement-boleto-fee-row-info">
                      <strong>#{title.id}</strong>
                      <span>{title.entity || title.description}</span>
                      <span>Face / saldo: {formatCurrency(remaining)}</span>
                    </div>
                    <label className="cash-statement-boleto-fee-field">
                      <span>Taxa (R$)</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        value={boletoFeeConfirm.fees[title.id] ?? ''}
                        onChange={(e) => {
                          const value = e.target.value
                          setBoletoFeeConfirm((prev) =>
                            prev
                              ? { ...prev, fees: { ...prev.fees, [title.id]: value }, localError: null }
                              : prev
                          )
                        }}
                      />
                    </label>
                    <div className="cash-statement-boleto-fee-net">
                      <span>Líquido</span>
                      <strong>{formatCurrency(net)}</strong>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="cash-statement-partial-modal-actions">
              <button
                type="button"
                className="cash-statement-partial-modal-cancel"
                onClick={() => setBoletoFeeConfirm(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="cash-statement-partial-modal-confirm full"
                onClick={handleConfirmBoletoFeeLink}
              >
                Confirmar vínculo com taxa
              </button>
            </div>
          </div>
        </div>
      )}

      {isDigitalAccount && batchSelectedTransactions.length > 1 && (
        <aside className="cash-statement-batch-dock" aria-label="Lançar contas a pagar em lote">
          <div className="cash-statement-batch-dock-header">
            <div>
              <p className="cash-statement-batch-dock-title">Lançar em lote</p>
              <p className="cash-statement-batch-dock-count">
                {batchSelectedTransactions.length} lançamentos · {formatCurrency(batchTotal)}
              </p>
            </div>
            <button
              type="button"
              className="cash-statement-batch-dock-close"
              onClick={clearBatchSelection}
              disabled={batchSubmitting}
              aria-label="Limpar seleção"
            >
              <X size={16} />
            </button>
          </div>

          <p className="cash-statement-batch-dock-hint">
            Categoria, descrição, subcategoria e centro de custo valem para todos. Cada conta sai com a data e o valor do extrato.
          </p>

          <ul className="cash-statement-batch-dock-list">
            {batchSelectedTransactions.map((transaction) => (
              <li key={getStatementRowKey(transaction)}>
                <span>{formatDateOnly(transaction.transaction_date)}</span>
                <strong>{formatCurrency(Math.abs(parseFloat(transaction.amount || 0)))}</strong>
              </li>
            ))}
          </ul>

          <label className="cash-statement-batch-field">
            <span>Pessoa</span>
            <button
              type="button"
              className="cash-statement-batch-person"
              onClick={() => setBatchPersonModalOpen(true)}
              disabled={batchSubmitting}
            >
              {batchForm.personName || 'Selecionar pessoa'}
            </button>
          </label>

          <label className="cash-statement-batch-field">
            <span>Descrição</span>
            <input
              type="text"
              value={batchForm.description}
              onChange={(event) => setBatchForm((prev) => ({ ...prev, description: event.target.value }))}
              disabled={batchSubmitting}
              placeholder="Descrição única do lote"
            />
          </label>

          <FilterLeftPicker
            label="Categoria"
            title="Categoria"
            value={batchForm.categoryId}
            options={batchParentCategories.map((category) => ({
              value: String(category.id),
              label: category.name
            }))}
            onChange={(value) => setBatchForm((prev) => ({
              ...prev,
              categoryId: value,
              subcategoryId: ''
            }))}
            emptyOptionLabel="Selecione uma categoria"
            searchable
            searchPlaceholder="Buscar categoria..."
            disabled={batchSubmitting}
          />

          <FilterLeftPicker
            label="Subcategoria"
            title="Subcategoria"
            value={batchForm.subcategoryId}
            options={batchSubcategories.map((category) => ({
              value: String(category.id),
              label: category.name
            }))}
            onChange={(value) => setBatchForm((prev) => ({ ...prev, subcategoryId: value }))}
            emptyOptionLabel={
              !batchForm.categoryId
                ? 'Selecione uma categoria'
                : batchSubcategories.length === 0
                  ? 'Sem subcategorias'
                  : 'Selecione uma subcategoria'
            }
            searchable={batchSubcategories.length > 0}
            searchPlaceholder="Buscar subcategoria..."
            disabled={batchSubmitting || !batchForm.categoryId || batchSubcategories.length === 0}
          />

          <FilterLeftPicker
            label="Centro de custo"
            title="Centro de custo"
            value={batchForm.costCenterId}
            options={batchCostCenters.map((center) => ({
              value: String(center.id),
              label: center.name
            }))}
            onChange={(value) => setBatchForm((prev) => ({ ...prev, costCenterId: value }))}
            emptyOptionLabel="Selecione um centro de custo"
            searchable
            searchPlaceholder="Buscar centro de custo..."
            disabled={batchSubmitting}
          />

          {batchError && <p className="cash-statement-batch-error">{batchError}</p>}

          <button
            type="button"
            className="cash-statement-batch-submit"
            onClick={handleBatchLaunchPayables}
            disabled={batchSubmitting}
          >
            {batchSubmitting ? <Loader2 size={16} className="cash-statement-batch-spinner" /> : <Plus size={16} />}
            Lançar contas
          </button>
        </aside>
      )}

      <SelectPersonModal
        isOpen={batchPersonModalOpen}
        onClose={() => setBatchPersonModalOpen(false)}
        selectedType={batchForm.personType}
        selectedId={batchForm.personId}
        onSelect={(person) => {
          setBatchForm((prev) => ({
            ...prev,
            personType: person.type,
            personId: person.id,
            personName: person.name
          }))
          setBatchPersonModalOpen(false)
        }}
      />
    </div>
  )
}

export default CashStatementPage
